import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { setActiveLocale } from '../features/format.js';

/**
 * Minimal, dependency-free i18n + direction layer. English and Arabic are
 * first-class (PRODUCT.md): switching locale flips `document.dir` so the whole
 * glass shell mirrors via its logical properties, and sets `lang`/`dir` so
 * bidi-sensitive content renders correctly. Formatting helpers (money, numbers,
 * dates) read the active locale so JOD and other currencies format natively.
 *
 * The dictionary is intentionally small — it covers the shell chrome and the
 * language toggle. Feature copy stays in-component; this is the frame, not a
 * full translation pass (recorded as follow-up).
 */
export type Locale = 'en' | 'ar';

const DICT: Record<Locale, Record<string, string>> = {
  en: {
    'nav.clients': 'Clients',
    'nav.brands': 'Brands',
    'nav.workspaceUsers': 'Users',
    'nav.settings': 'Settings',
    'nav.ingestion': 'Ingestion',
    'nav.monthlyOffers': 'Monthly offers',
    'nav.campaignsOverview': 'Campaigns overview',
    'nav.payoutSchedule': 'Payout schedule',
    'nav.payoutBacktest': 'Backtest (dev)',
    'nav.subscriptions': 'Billing',
    'nav.dashboard': 'Dashboard',
    'nav.orders': 'Orders',
    'nav.statements': 'Statements',
    'nav.payouts': 'Payouts',
    'nav.campaigns': 'Campaigns',
    'nav.insights': 'Insights',
    'nav.notifications': 'Notifications',
    'nav.catalog': 'Catalog',
    'nav.onboarding': 'Onboarding',
    'nav.data': 'Data',
    // ── Smart notifications (clarity-smart-notifications) ──
    'notif.title': 'Notifications',
    'notif.sub':
      'Everything that needs your eyes — campaigns, money, menu and store health — pushed to you so you never have to go looking.',
    'notif.open': 'Open notifications',
    'notif.tab.inbox': 'Inbox',
    'notif.tab.reports': 'Reports',
    'notif.tab.prefs': 'Preferences',
    'notif.markAllRead': 'Mark all read',
    'notif.markRead': 'Mark read',
    'notif.viewAll': 'View all notifications',
    'notif.filter.all': 'All',
    'notif.filter.action': 'Needs action',
    'notif.filter.campaign': 'Campaigns',
    'notif.filter.money': 'Money',
    'notif.filter.menu': 'Menu',
    'notif.filter.health': 'Store health',
    'notif.filter.report': 'Reports',
    'notif.empty.title': 'All caught up',
    'notif.empty.body':
      'Nothing needs your attention right now. We’ll nudge you the moment something matters.',
    'notif.unread': 'unread',
    'notif.allRead': 'all read',
    'notif.day.today': 'Today',
    'notif.day.yesterday': 'Yesterday',
    'notif.day.earlier': 'Earlier',
    'notif.reports.empty': 'No reports yet. Your first recap arrives on your chosen cadence.',
    'notif.prefs.eventsTitle': 'What to tell me about',
    'notif.prefs.eventsSub': 'Pick the moments worth a nudge, and where each one should reach you.',
    'notif.prefs.reportTitle': 'Periodic report',
    'notif.prefs.reportSub': 'A written summary so you see the whole picture without asking.',
    'notif.prefs.cadence': 'How often',
    'notif.prefs.channelsTitle': 'Channels',
    'notif.prefs.channelsSub':
      'Where Clarity can reach you. WhatsApp and the website chat share one thread.',
    'notif.prefs.balanceTitle': 'Balance alert threshold',
    'notif.prefs.balanceSub': 'Tell me when estimated balance owed crosses this amount.',
    'notif.chan.inapp': 'In-app',
    'notif.chan.push': 'Push',
    'notif.chan.whatsapp': 'WhatsApp',
    'notif.cadence.3day': 'Every 3 days',
    'notif.cadence.weekly': 'Weekly',
    'notif.cadence.monthly': 'Monthly',
    'notif.cadence.off': 'Off',
    'notif.push.enable': 'Enable browser notifications',
    'notif.push.enabled': 'Browser notifications on',
    'notif.push.unsupported': 'Your browser doesn’t support push notifications.',
    'notif.push.blocked': 'Notifications are blocked in your browser settings.',
    'notif.save': 'Save preferences',
    'notif.saved': 'Preferences saved',
    'notif.event.campaign_finished': 'Campaign finished',
    'notif.event.campaigns_ready_for_review': 'Campaigns ready for review',
    'notif.event.new_menu_item': 'New menu item to decide on',
    'notif.event.payout_mismatch': 'A payout didn’t match',
    'notif.event.store_offline': 'A branch went offline',
    'notif.event.rating_slipped': 'Rating slipped',
    'notif.event.ingestion_stalled': 'A store stopped syncing',
    'notif.event.balance_threshold': 'Balance owed crosses a threshold',
    'rail.manage': 'Manage',
    'rail.workspace': 'Workspace',
    'rail.viewing': 'Viewing client',
    'rail.back': 'Back to all clients',
    'rail.switch': 'Switch client',
    'topbar.signout': 'Sign out',
    'lang.toggle': 'العربية',
    // ── Scope switcher (clarity-navigation-scope Req 2) ──
    'scope.all': 'All stores',
    'scope.label': 'Scope',
    'scope.switch': 'Switch scope',
    'scope.open': 'Change what you’re viewing',
    'scope.search': 'Search branches',
    'scope.search.placeholder': 'Search by name or store id',
    'scope.search.empty': 'No branches match your search.',
    'scope.summary.branches': 'branches',
    'scope.summary.branch': 'branch',
    'scope.empty': 'No branches yet',
    'scope.empty.cta': 'Import a brand',
    'scope.loading': 'Loading branches…',
    'scope.lifecycle.live': 'live',
    'scope.lifecycle.onboarding': 'onboarding',
    'scope.lifecycle.disabled': 'disabled',
    'scope.lifecycle.removed': 'removed',
    // The chip's state is spelled out for assistive tech so the colour is never
    // the only carrier of meaning (Req 2.5, WCAG AA). Rendered via {label}.
    'scope.lifecycle.aria': 'lifecycle: {label}',
    // ── Rail nav (clarity-navigation-scope task 4.2) ──
    // The "per store" hint on a branch-only surface shown at an all/brand scope
    // (Req 4.3): the surface still links, but the hint names the constraint
    // (text, not colour alone — WCAG AA) before the user clicks.
    'nav.perStore': 'per store',
    'nav.perStore.aria': 'opens per store — pick a branch',
    // ── Pick-a-branch prompt (clarity-navigation-scope Req 4.2 / 4.5) ──
    // Shown when a branch-only surface (catalog / onboarding) is opened at an
    // All / Brand scope spanning more than one branch: the surface is per-store,
    // so the user narrows to one branch here.
    'pickBranch.title': 'Pick a branch',
    'pickBranch.subtitle': '{surface} is a per-store view. Choose a branch to open it.',
    'pickBranch.aria': 'Pick a branch for {surface}',
    // ── Onboarding lockdown note (clarity-navigation-scope Req 5.2) ──
    // Surfaced when the active scope is a single not-yet-onboarded branch: only
    // its onboarding is offered until setup completes.
    'scope.lockdown.note': 'Finish onboarding this branch to unlock its other views.',
    // ── Combined-view notes (clarity-navigation-scope Req 6.2) ──
    // Placeholder keys for the aggregated All / Brand surfaces that tasks 6 wire
    // up: how many in-scope branches are still onboarding (and so excluded from
    // the data reads). `{count}` is interpolated by the caller.
    'scope.combined.onboarding.one': '{count} branch still onboarding',
    'scope.combined.onboarding.many': '{count} branches still onboarding',
    // ── Admin ingestion panel (admin-ingestion-control Req 5) ──
    'ingestion.title': 'Ingestion',
    'ingestion.sub': 'Recent per-store ingestion runs across every tenant, newest first.',
    'ingestion.mode.label': 'Mode',
    'ingestion.mode.daily': 'Daily',
    'ingestion.mode.backfill': 'Backfill',
    'ingestion.mode.reference': 'Reference window',
    'ingestion.mode.daily.hint': 'Incremental pull since each store’s last ingest.',
    'ingestion.mode.backfill.hint': 'Full-window re-pull of the whole history.',
    'ingestion.runAll': 'Run now (all stores)',
    'ingestion.runAll.pending': 'Queueing…',
    'ingestion.rerun': 'Re-run',
    'ingestion.rerun.aria': 'Re-run {store} in {mode} mode',
    'ingestion.triggered.one': 'Queued {count} store in batch {batch}.',
    'ingestion.triggered.many': 'Queued {count} stores in batch {batch}.',
    'ingestion.triggerError': 'Could not start the run. Try again.',
    // Table
    'ingestion.col.store': 'Store',
    'ingestion.col.when': 'When',
    'ingestion.col.covered': 'Covered',
    'ingestion.covered.none': '—',
    'ingestion.covered.aria': 'Covered data window {range}',
    'ingestion.col.trigger': 'Trigger',
    'ingestion.col.mode': 'Mode',
    'ingestion.col.result': 'Result',
    'ingestion.col.persisted': 'Persisted',
    'ingestion.col.actions': 'Actions',
    // Trigger + mode values rendered in-cell
    'ingestion.trigger.scheduled': 'Scheduled',
    'ingestion.trigger.manual': 'Manual',
    'ingestion.trigger.reference': 'Onboarding gather',
    // Status
    'ingestion.status.running': 'Running',
    'ingestion.status.succeeded': 'Succeeded',
    'ingestion.status.failed': 'Failed',
    'ingestion.status.skipped': 'Skipped',
    'ingestion.status.aria': 'status: {label}',
    // Result cell detail
    'ingestion.advanced': 'Advanced',
    'ingestion.notAdvanced': 'No new data',
    'ingestion.failedSources': 'Failed sources: {sources}',
    'ingestion.running.note': 'In progress — this row updates as it finishes.',
    // Persisted-count labels (compact, with a full aria label)
    'ingestion.persisted.orders': 'orders',
    'ingestion.persisted.statements': 'statements',
    'ingestion.persisted.payouts': 'payouts',
    'ingestion.persisted.provisioned': 'provisioned',
    'ingestion.persisted.campaigns': 'campaigns',
    'ingestion.persisted.matched': 'matched',
    'ingestion.persisted.rawJson': 'verbatim',
    'ingestion.persisted.aria':
      '{orders} orders, {statements} statements, {payouts} payouts, {provisioned} provisioned items, {campaigns} campaigns, {matched} orders matched, {rawJson} captured verbatim',
    // Empty
    'ingestion.empty': 'No ingestion runs yet. Press “Run now” to start one.',
    // Retry affordances
    'ingestion.retry': 'Retry',
    'ingestion.retry.pending': 'Retrying…',
    'ingestion.retry.aria': 'Retry {store}',
    'ingestion.retryAllFailed': 'Retry all failed',
    'ingestion.retryAllFailed.pending': 'Retrying…',
    'ingestion.retryAllFailed.count': 'Retry all failed ({count})',
    'ingestion.summary.running': '{count} running',
    'ingestion.summary.failed': '{count} failed',
    'ingestion.summary.succeeded': '{count} succeeded',
    'ingestion.card.when': 'Started',
    'ingestion.card.covered': 'Covered',
    'ingestion.card.nothing': 'Nothing persisted yet.',
    // ── Admin monthly-offer review (clarity-campaign-matching Req 3.3, 5.2, 10.3) ──
    'offerReview.title': 'Monthly offers',
    'offerReview.sub':
      'Upload the account manager’s offer sheet, review and correct the draft, then save it. Monthly offers are shared across every store on the platform and effective-dated — a new month appends, it never rewrites.',
    'offerReview.platform': 'Platform',
    'offerReview.image': 'Offer-sheet image',
    'offerReview.intake.hint':
      'Pick a platform and the offer-sheet image. We read it with OCR into an editable draft; with OCR off you enter the offers by hand.',
    'offerReview.ocr.pending': 'Reading the offer sheet…',
    'offerReview.ocr.error': 'Could not read that image. Try another, or enter the offers by hand.',
    'offerReview.banner.ocr':
      'Transcribed from the image. Review every field against the sheet before saving.',
    'offerReview.banner.noKey':
      'OCR is off, so the draft is empty — add each offer from the sheet by hand below.',
    'offerReview.banner.noKeyNonEmpty':
      'OCR is off; these offers were entered by hand. Review them before saving.',
    'offerReview.start.title': 'Start a monthly-offer draft',
    'offerReview.start.sub':
      'Upload an offer sheet above to OCR it, or start a blank draft and enter the offers by hand.',
    'offerReview.start.manual': 'Enter offers by hand',
    'offerReview.editor.title': 'Review the draft',
    'offerReview.editor.count.one': '{count} offer',
    'offerReview.editor.count.many': '{count} offers',
    'offerReview.editor.empty': 'No offers in the draft yet. Add one to start.',
    'offerReview.offer.add': 'Add an offer',
    'offerReview.offer.aria': 'Offer {n}',
    'offerReview.offer.heading': 'Offer {n}',
    'offerReview.offer.remove': 'Remove',
    'offerReview.offer.remove.aria': 'Remove offer {n}',
    'offerReview.field.name': 'Name',
    'offerReview.field.name.ph': 'e.g. Flash Sale — All customers',
    'offerReview.field.arabic': 'Arabic name',
    'offerReview.field.arabic.ph': 'الاسم بالعربية',
    'offerReview.field.audience': 'Audience',
    'offerReview.audience.all': 'All customers',
    'offerReview.audience.plus': 'Plus (TPRO) only',
    'offerReview.audience.nonplus': 'Non-Plus only',
    'offerReview.field.discount': 'Discount %',
    'offerReview.field.cap': 'Max discount cap',
    'offerReview.field.cofund': 'Co-fund %',
    'offerReview.field.minOrder': 'Min order',
    'offerReview.field.dealKey': 'Platform deal key',
    'offerReview.field.dealKey.ph': 'e.g. LQTA-2026',
    'offerReview.field.dealKey.hint':
      'Tag the matching platform deal so a monthly offer and its platform deal dedupe into one campaign.',
    'offerReview.occ.title': 'Dates',
    'offerReview.occ.count.one': '{count} date',
    'offerReview.occ.count.many': '{count} dates',
    'offerReview.occ.empty': 'No dates yet — add each run of this offer.',
    'offerReview.occ.add': 'Add a date',
    'offerReview.occ.aria': 'Date {n}',
    'offerReview.occ.start': 'Start',
    'offerReview.occ.end': 'End',
    'offerReview.occ.discount': 'Discount % (override)',
    'offerReview.occ.cap': 'Cap (override)',
    'offerReview.occ.inherit': 'inherits offer',
    'offerReview.occ.remove': 'Remove',
    'offerReview.occ.remove.aria': 'Remove date {n}',
    'offerReview.save': 'Save monthly offers',
    'offerReview.save.pending': 'Saving…',
    'offerReview.save.error': 'Could not save. Review the draft and try again.',
    'offerReview.result.title': 'Saved',
    'offerReview.result.lede':
      'Saved {campaigns} campaigns and {occurrences} dates, shared across every {platform} store.',
    'offerReview.result.warnings': 'Warnings',
    'offerReview.saved.tagged': 'tagged {key}',
    'offerReview.saved.untagged': 'not tagged',
    // ── Current offers catalog (read of what's saved) ──
    'offerReview.current.title': 'Current offers',
    'offerReview.current.sub': '· what’s live for {platform} right now',
    'offerReview.current.effNote':
      '{count} · current versions · shared across all stores',
    'offerReview.current.one': '1 offer',
    'offerReview.current.many': '{count} offers',
    'offerReview.current.filter': 'Filter offers…',
    'offerReview.current.loading': 'Loading saved offers…',
    'offerReview.current.error': 'Could not load the saved offers.',
    'offerReview.current.empty':
      'No current offers for this platform. Upload a sheet or enter offers by hand to start the catalog.',
    'offerReview.current.noFilterMatch': 'No offers match your filter.',
    'offerReview.current.ranged': 'ranged',
    'offerReview.current.tproOnly': 'TPRO only',
    'offerReview.current.newOnly': 'new only',
    'offerReview.current.noDiscount': 'no set discount',
    'offerReview.current.off': '{disc} off',
    'offerReview.current.cap': 'cap {cap} JOD',
    'offerReview.current.runs.one': '1 run',
    'offerReview.current.runs.many': '{count} runs',
    'offerReview.current.occHeading': 'Occurrences',
    'offerReview.current.occCol': 'Occurrence',
    'offerReview.current.occOverrideCol': 'Per-run override',
    'offerReview.current.inherits': 'inherits offer',
    'offerReview.current.expand': 'Show occurrences for {name}',
    'offerReview.current.filterPlatform': 'Filter by platform',
    'offerReview.current.allPlatforms': 'All platforms',
    // ── List-primary restructure: action buttons + modals ──
    'offerReview.action.add': 'Add campaign',
    'offerReview.action.import': 'Import campaigns',
    'offerReview.add.title': 'Add a monthly offer',
    'offerReview.add.sub':
      'Enter one offer by hand. It’s shared across the platform’s stores and effective-dated.',
    'offerReview.import.title': 'Import campaigns from a sheet',
    'offerReview.import.sub':
      'Upload the account manager’s offer sheet. We OCR it into an editable draft you review before saving.',
    'offerReview.import.dropTitle': 'Choose an offer-sheet image',
    'offerReview.import.dropHint': 'JPEG or PNG. We read it with OCR into a draft you review before saving.',
    'offerReview.modal.close': 'Close',
    'offerReview.field.discountMin': 'Discount — min %',
    'offerReview.field.discountMax': 'Discount — max % (optional)',
    // ── Admin connections (clarity-platform-abstraction Req 5) ──
    'nav.connections': 'Connections',
    'connections.title': 'Platform connections',
    'connections.adminOnly': 'Admin only',
    'connections.lede':
      'Clarity authenticates each platform the way that platform works. Talabat and Careem read a live browser session you stay logged into; MyThings signs in per store with stored credentials. Nothing sensitive is shown here — statuses are a classification only.',
    'connections.authModel': 'Browser session · Chrome',
    'connections.groupSession': 'Browser-session platforms',
    'connections.authRest': 'Stored credentials · REST API',
    'connections.storesCount': '{n} stores · each signs in on its own',
    'connections.detailRest':
      'Each store signs in to MyThings with its own account. A store is connected while its stored credentials still mint a token.',
    'connections.notSignedIn': '— not yet signed in',
    'connections.enterCreds': 'Enter credentials',
    'connections.update': 'Update credentials',
    'connections.storeState.connected': 'Connected',
    'connections.storeState.expired': 'Login expired',
    'connections.storeState.needs-credentials': 'Needs credentials',
    'connections.storeState.checking': 'Checking…',
    'connections.storeState.error': 'Connection error',
    'connections.retry': 'Retry sign-in',
    'connections.state.needs-credentials': 'Needs credentials',
    'connections.state.partial': 'Action needed',
    'connections.state.checking': 'Checking…',
    'connections.cred.titleNew': 'Connect store',
    'connections.cred.titleUpd': 'Update credentials',
    'connections.cred.leadNew':
      'Enter the MyThings account this store signs in with. Clarity stores it encrypted and replays it to keep the store’s session alive — it is never shown again.',
    'connections.cred.leadUpd':
      'Update the MyThings login for this store. The new password replaces the stored one; Clarity re-signs-in immediately to confirm it works.',
    'connections.cred.loginLabel': 'Account login',
    'connections.cred.pwLabel': 'Password',
    'connections.cred.reveal': 'Show',
    'connections.cred.hide': 'Hide',
    'connections.cred.note':
      'Stored with reversible encryption (not shown again) because Clarity must replay it to mint a fresh access token when the old one expires.',
    'connections.cred.save': 'Save & connect',
    'connections.cred.update': 'Update & re-connect',
    'connections.cred.cancel': 'Cancel',
    'connections.checked': 'checked',
    'connections.loading': 'Reading the live sessions…',
    'connections.error': 'Could not load the connection status. Try again.',
    'connections.empty.title': 'No live platforms yet',
    'connections.empty.sub': 'A platform appears here once its connector is wired and marked live.',
    'connections.reconnect': 'Reconnect',
    'connections.howItWorks': 'How it works',
    'connections.howItWorks.aria': 'How the {platform} connection works',
    // State pills (text, not colour alone — WCAG AA)
    'connections.state.connected': 'Connected',
    'connections.state.expired': 'Session expired',
    'connections.state.disconnected': 'Disconnected',
    'connections.state.aria': 'connection state: {label}',
    // The shell rollup (names every live platform with a status dot — Req 5.3)
    'connections.rollup.checking': 'Checking connections…',
    'connections.rollup.unavailable': 'Connections unavailable',
    'connections.rollup.aria': 'Platform connections — open the connections panel',
    'connections.rollup.title': 'Open platform connections',
    // Per-state detail sentence under each card head
    'connections.detail.connected': 'A logged-in portal session is readable in the linked Chrome.',
    'connections.detail.expired':
      'The session is no longer valid — a human must re-log-in in the linked Chrome to reconnect.',
    'connections.detail.disconnected':
      'No session: the browser layer is off, Chrome is unreachable, or no portal tab is open.',
    // The one shared re-auth flow (no paste UI — recovery is automatic)
    'connections.reauth.title': 'Reconnect {platform}',
    'connections.reauth.lead':
      '{platform} is cookie-validated behind anti-bot protection, so there’s nothing to paste — Clarity reads the session from the Chrome window you stay logged into.',
    'connections.reauth.step1': 'Open the linked Chrome window on the acquisition host.',
    'connections.reauth.step2': 'Go to the {platform} partner portal and sign in as usual.',
    'connections.reauth.step3':
      'Leave the tab open — Clarity keeps the session warm and re-reads it automatically.',
    'connections.reauth.recovery':
      'Recovery is automatic. Once you’re logged back in, the next status check flips this platform to connected — nothing to paste, upload, or store.',
    'connections.reauth.close': 'Close',
    'connections.reauth.openChrome': 'Open linked Chrome',
    // ── Manual MyThings add (mythings-manual-add Req 10) ──
    'addMythings.card.title': 'Add a MyThings store',
    'addMythings.card.body':
      "Enter the brand and each branch's MyThings login. Clarity signs in, pulls the store id and name automatically, and connects it right away — no Chrome session needed.",
    'addMythings.card.meta': 'MyThings · stored credentials',
    'addMythings.card.open': 'Add MyThings store',
    'addMythings.sheet.title': 'Add a MyThings store',
    'addMythings.sheet.client': 'Client',
    'addMythings.sheet.clientPlaceholder': 'Select a client…',
    'addMythings.sheet.brand': 'Brand name',
    'addMythings.sheet.brandPlaceholder': 'e.g. Burger Boutique',
    'addMythings.sheet.brandHint': 'The chain this store belongs to. Branches are added below.',
    'addMythings.sheet.branches': 'Branches',
    'addMythings.sheet.branch': 'Branch',
    'addMythings.sheet.branchName': 'Branch name',
    'addMythings.sheet.branchNamePlaceholder': 'e.g. Abdoun',
    'addMythings.sheet.substore': 'MyThings store id',
    'addMythings.sheet.substoreHint': '(SubStoreId)',
    'addMythings.sheet.substorePlaceholder': '17755',
    'addMythings.sheet.login': 'Account login',
    'addMythings.sheet.loginPlaceholder': 'store@mythingsjo.com',
    'addMythings.sheet.password': 'Password',
    'addMythings.sheet.reveal': 'Show',
    'addMythings.sheet.hide': 'Hide',
    'addMythings.sheet.removeBranch': 'Remove',
    'addMythings.sheet.addBranch': 'Add another branch',
    'addMythings.sheet.callout':
      "We sign in with each login now to pull the store's MyThings id and name — you don't enter those. Each login is stored with reversible encryption and replayed to keep the branch signed in; it's never shown again.",
    'addMythings.sheet.count.one': '{count} branch',
    'addMythings.sheet.count.many': '{count} branches',
    'addMythings.sheet.cancel': 'Cancel',
    'addMythings.sheet.submit': 'Add & connect',
    'addMythings.sheet.submitting': 'Connecting…',
    'addMythings.sheet.error': 'Could not add the store. Review the branches and try again.',
    'addMythings.done.title': '{brand} is connected',
    'addMythings.done.sub.one': '{count} branch added and signed in. You can manage them any time on Connections.',
    'addMythings.done.sub.many': '{count} branches added and signed in. You can manage them any time on Connections.',
    'addMythings.done.connected': 'Connected',
    'addMythings.done.expired': 'Login expired',
    'addMythings.done.error': 'Connection error',
    'addMythings.done.unnamed': 'Branch',
    'addMythings.done.notSignedIn': 'not yet signed in',
    'addMythings.done.close': 'Close',
    'addMythings.done.goConnections': 'Go to Connections',
    // Entry hub header (two "add" entry cards)
    'addHub.title': 'Add a store',
    'addHub.lede':
      "Discover stores from a platform you log into (Talabat, Careem), or add a MyThings store by hand — MyThings signs in per store with its own credentials, so there's nothing to auto-discover.",
    'addHub.import.title': 'Discover & import',
    'addHub.import.body':
      'Pull a brand and its branches from a platform account you stay logged into in the linked Chrome.',
    'addHub.import.meta': 'Talabat · Careem',
    // ── Admin payout schedule (clarity-payout-schedule Req 5) ──
    'payoutSchedule.title': 'Payout schedule',
    'payoutSchedule.sub':
      'Talabat’s payout calendar per month — coverage windows and when each is paid. Saved once and shared across every store on the platform.',
    'payoutSchedule.add': '+ Add schedule',
    'payoutSchedule.list.platform': 'Platform',
    'payoutSchedule.list.month': 'Month',
    'payoutSchedule.list.rows': 'Rows',
    'payoutSchedule.list.coverage': 'Coverage',
    'payoutSchedule.list.updated': 'Last updated',
    'payoutSchedule.list.edit': 'Edit',
    'payoutSchedule.list.showPayouts': 'Show payouts',
    'payoutSchedule.list.hidePayouts': 'Hide payouts',
    'payoutSchedule.list.empty.title': 'No schedules yet',
    'payoutSchedule.list.empty.body':
      'Add Talabat’s monthly payout calendar so every store shows its next payout.',
    'payoutSchedule.list.loading': 'Loading schedules…',
    'payoutSchedule.list.error': 'Could not load schedules. Try again.',
    'payoutSchedule.modal.addTitle': 'Add payout schedule',
    'payoutSchedule.modal.editTitle': 'Edit payout schedule',
    'payoutSchedule.modal.close': 'Close',
    'payoutSchedule.cancel': 'Cancel',
    'payoutSchedule.platform': 'Platform',
    'payoutSchedule.month': 'Month',
    'payoutSchedule.col.type': 'Type',
    'payoutSchedule.col.from': 'Coverage from',
    'payoutSchedule.col.to': 'Coverage to',
    'payoutSchedule.col.payout': 'Payout date',
    'payoutSchedule.col.label': 'Label',
    'payoutSchedule.col.actions': 'Actions',
    'payoutSchedule.type.window': 'Coverage window',
    'payoutSchedule.type.closing': 'Monthly closing',
    'payoutSchedule.row': 'Row {n}',
    'payoutSchedule.labelPlaceholder': 'Optional note',
    'payoutSchedule.addRow': '+ Add row',
    'payoutSchedule.removeRow': 'Remove row',
    'payoutSchedule.save': 'Save schedule',
    'payoutSchedule.saving': 'Saving…',
    'payoutSchedule.saveError': 'Could not save. Check the rows and try again.',
    'payoutSchedule.saved': 'Saved {count} schedule rows, shared across every store on the platform.',
    'payoutSchedule.hint':
      'Transcribe the Talabat schedule table (coverage period → transfer date). A “monthly closing” row settles the prior month and needs no coverage window.',
    // ── Payout estimate backtest (INTERNAL dev tool; clarity-payout-backtest) ──
    'backtest.title': "Payout reconciliation backtest",
    'backtest.dev': "dev",
    'backtest.sub': "Compares Talabat’s own payout breakdown, line by line, with the same lines built from the payout window’s orders — so a gap points at the exact component that differs.",
    'backtest.stat.passRate': "Pass rate",
    'backtest.stat.passRateSub': "{pass} of {judged} payouts within ±{tol}%",
    'backtest.stat.tested': "Payouts tested",
    'backtest.stat.testedSub': "settled, across {stores} store(s)",
    'backtest.stat.passing': "Passing",
    'backtest.stat.passingSub': "our net within ±{tol}% of Talabat’s",
    'backtest.stat.failing': "Failing",
    'backtest.stat.failingSub': "our net off by more than ±{tol}%",
    'backtest.tolerance': "Tolerance ±",
    'backtest.show': "Show",
    'backtest.show.all': "All payouts",
    'backtest.show.fail': "Failing only",
    'backtest.show.pass': "Passing only",
    'backtest.col.payout': "Payout",
    'backtest.col.period': "Period",
    'backtest.col.paidOn': "Paid on",
    'backtest.col.orders': "Orders",
    'backtest.col.talabatNet': "Talabat net",
    'backtest.col.ourNet': "Our net",
    'backtest.col.delta': "Δ",
    'backtest.col.deltaPct': "Δ %",
    'backtest.col.verdict': "Verdict",
    'backtest.verdict.pass': "Pass",
    'backtest.verdict.fail': "Fail",
    'backtest.detail.window': "Covers {from} → {to}",
    'backtest.detail.paidOn': "Paid on",
    'backtest.detail.orders': "Orders: {linked} of {expected} linked ({cancelled} cancelled)",
    'backtest.detail.noBreakdown': "Talabat breakdown not captured for this payout — re-run the backfill to fetch it",
    'backtest.lines.line': "Line",
    'backtest.lines.talabat': "Talabat",
    'backtest.lines.ours': "Ours (from orders)",
    'backtest.lines.diff': "Ours − Talabat",
    'backtest.lines.accountLevel': "account-level",
    'backtest.lines.legend': "Amounts are signed as Talabat reports them (deductions negative). “Account-level” lines have no order behind them, so we can’t build them from orders — they show what Talabat added or deducted on its own. Adjustments carry a skipped round (under the minimum payout): we build it from that round’s orders, minus its Talabat Ads spend (+16% VAT) and an estimate of its sponsored-deal fees, which Ads reporting doesn’t include.",
    'backtest.line.sales': "Sales (gross + tax)",
    'backtest.line.talabatVoucher': "Talabat-funded vouchers",
    'backtest.line.commission': "Commission",
    'backtest.line.paymentFee': "Payment fee",
    'backtest.line.proDelivery': "Pro delivery fee",
    'backtest.line.operational': "Wait-time fee",
    'backtest.line.ads': "Ads / marketing",
    'backtest.line.refundCharges': "Customer refund charges",
    'backtest.line.adjustments': "Adjustments (carried-in round)",
    'backtest.carry.orders': "Skipped round {from} → {to} · {n} orders",
    'backtest.carry.inferred': "start inferred from the first order on file",
    'backtest.carry.ads': "Ads not reported yet (estimated) · {n} × {rate} per order",
    'backtest.carry.reportedAds': "Ads spend reported by Talabat Ads (+16% VAT)",
    'backtest.carry.perOrder': "Per-order marketing already in the export",
    'backtest.carry.deals': "Estimated deal fees · {n} × {rate} per order",
    'backtest.adspend.reported': "Skipped rounds priced with Talabat Ads spend ({from} → {to}); only sponsored-deal fees are estimated",
    'backtest.adspend.unavailable': "Talabat Ads spend not fetched ({reason}), so skipped rounds' ads are estimated",
    'backtest.carry.tag': "+ carried round",
    'backtest.carry.none': "No skipped round before this payout",
    'backtest.min.label': "{min} JOD minimum",
    'backtest.min.holds': "rule holds",
    'backtest.min.breaks': "rule doesn’t fit",
    'backtest.min.paidBelow': "paid {amount}, under the minimum",
    'backtest.min.carriedAbove': "carried {amount}, at or over the minimum",
    'backtest.stat.minRule': "Minimum rule",
    'backtest.stat.minRuleSub': "{min} JOD rule fits {held} of {total} payouts",
    'backtest.line.otherAdditions': "Other additions",
    'backtest.line.otherDeductions': "Other deductions",
    'backtest.line.net': "Net payout",
    'backtest.empty': "No settled payouts to backtest yet.",
    'backtest.noMatch': "No payouts match this filter.",
    'backtest.loading': "Running backtest…",
    'backtest.error': "Could not load the backtest.",
    'backtest.store.meta': "{n} payouts · {platform}",
    // ── Banner options (clarity-banner-options, UI contract brand/banner-options-mock.html) ──
    // Key groups, EN + AR (parity guarded by i18n.banner-options.test.ts):
    //   nav.bannerOptions, nav.campaigns.newCount.{one,many}  admin rail item + owner Campaigns count aria
    //   bannerOptions.platform.*                              platform names for {platform}
    //   banners.untitled / where.placeholder / run.* / fee.* / badge.*   shared BannerOptionRow
    //   banners.tab / about.* / group.* / join.* / notice.*   owner "Banners you can join" tab
    //   banners.loading / error.* / retry / empty.*           owner tab states
    //   bannerOptions.title / sub / add / filter.* / count.*  admin page head + list
    //   bannerOptions.row.* / runs.col.* / reach.* / state.* / action.*   admin rows
    //   bannerOptions.editor.* / field.* / run.* / fieldError.* / preview.*   editor modal
    //   bannerOptions.toast.*                                 save / publish / drafts / delete toasts
    //   bannerOptions.loading / error.* / retry / empty.* / noMatch.*   admin states
    // Counts use `.one` / `.many` with {count}; dates and money arrive preformatted.
    'nav.bannerOptions': 'Banner options',
    'nav.campaigns.newCount.one': '{count} new banner option',
    'nav.campaigns.newCount.many': '{count} new banner options',
    'bannerOptions.platform.talabat': 'Talabat',
    'bannerOptions.platform.careem': 'Careem',
    'bannerOptions.platform.mythings': 'MyThings',
    // Shared row (owner tab + editor preview)
    'banners.untitled': 'Untitled banner',
    'banners.where.placeholder': 'Where it appears',
    'banners.run.dates': '{from} – {to}',
    'banners.run.days.one': '{count} day',
    'banners.run.days.many': '{count} days',
    'banners.run.fee': '{fee} JOD',
    'banners.run.runningNow': 'Running now',
    'banners.fee.range': '{min}–{max}',
    'banners.fee.currency': 'JOD',
    'banners.fee.perRun': 'per run',
    'banners.fee.forTheRun': 'for the run',
    'banners.badge.new': 'New',
    'banners.badge.askBy': 'Ask by {date}',
    // Owner tab
    'banners.tab': 'Banners you can join',
    'banners.about.offering': 'These are paid placements {platform} is offering restaurants.',
    'banners.about.noSignup': "Clarity doesn't sign you up",
    'banners.about.ask': '; if one suits you, ask your {platform} account manager.',
    'banners.about.fees': 'Fees are what {platform} charges for each run.',
    'banners.group.upcoming': 'Coming up',
    'banners.group.upcoming.count.one': '{count} option',
    'banners.group.upcoming.count.many': '{count} options',
    'banners.group.running': 'Running now',
    'banners.group.running.sub': 'you can still ask about the next run',
    'banners.join.open': 'How to join',
    'banners.join.hide': 'Hide',
    'banners.join.lead.before': "Clarity can't enroll you. Your",
    'banners.join.lead.manager': '{platform} account manager',
    'banners.join.lead.after': 'does. Send them this, or say it in your own words:',
    'banners.join.message':
      'Hi, I\'d like to join the {platform} "{name}" ({where}) for {from} – {to} at {fee} JOD.\nBranch: {branch} ({externalId}).\nWhat do you need from us?',
    'banners.join.copy': 'Copy message',
    'banners.join.copied': 'Copied',
    'banners.loading': 'Loading banner options',
    'banners.error.title': "Couldn't load banner options.",
    'banners.error.body': "Your campaigns aren't affected.",
    'banners.retry': 'Try again',
    'banners.empty.title': 'No banners on offer for {platform} right now',
    'banners.empty.body':
      "When {platform} offers a paid placement, the Clarity team adds it here and you'll see it marked New.",
    'banners.notice.count.one': '{count} new banner option',
    'banners.notice.count.many': '{count} new banner options',
    'banners.notice.on': 'on {platform}.',
    'banners.notice.see': 'See banners',
    // Admin page
    'bannerOptions.title': 'Banner options',
    'bannerOptions.sub':
      'Paid placements a platform is offering this season. Publish one and every branch on that platform sees it under Campaigns → Banners you can join. Owners enroll through their account manager, not in Clarity.',
    'bannerOptions.add': 'Add banner option',
    'bannerOptions.filter.platform.aria': 'Filter by platform',
    'bannerOptions.filter.platform.all': 'All platforms',
    'bannerOptions.filter.status.aria': 'Filter by status',
    'bannerOptions.filter.status.all': 'All',
    'bannerOptions.filter.status.published': 'Published',
    'bannerOptions.filter.status.draft': 'Drafts',
    'bannerOptions.filter.status.ended': 'Ended',
    'bannerOptions.count.one': '{count} option',
    'bannerOptions.count.many': '{count} options',
    'bannerOptions.row.askBy': 'Ask by {date}',
    'bannerOptions.row.noAskBy': 'No ask-by date',
    'bannerOptions.row.runs.one': '{count} run',
    'bannerOptions.row.runs.many': '{count} runs',
    'bannerOptions.runs.col.run': 'Run',
    'bannerOptions.runs.col.days': 'Days',
    'bannerOptions.runs.col.fee': 'Fee',
    'bannerOptions.reach.draft': 'Not visible to owners yet.',
    'bannerOptions.reach.ended': 'Ended — no longer shown to owners.',
    'bannerOptions.reach.published': 'Shown to every {platform} branch.',
    'bannerOptions.state.draft': 'Draft',
    'bannerOptions.state.published': 'Published',
    'bannerOptions.state.ended': 'Ended',
    'bannerOptions.action.publish': 'Publish',
    'bannerOptions.action.moveToDrafts': 'Move to drafts',
    'bannerOptions.action.edit': 'Edit',
    'bannerOptions.action.delete': 'Delete',
    'bannerOptions.action.error': "Couldn't update “{name}”. Try again.",
    // Editor
    'bannerOptions.editor.titleAdd': 'Add a banner option',
    'bannerOptions.editor.titleEdit': 'Edit “{name}”',
    'bannerOptions.editor.sub':
      'Owners on this platform will see it as something they can ask their account manager to join.',
    'bannerOptions.editor.close': 'Close',
    'bannerOptions.editor.cancel': 'Cancel',
    'bannerOptions.editor.saveDraft': 'Save as draft',
    'bannerOptions.editor.moveToDrafts': 'Move to drafts',
    'bannerOptions.editor.publish': 'Publish',
    'bannerOptions.editor.saveChanges': 'Save changes',
    'bannerOptions.editor.delete': 'Delete',
    'bannerOptions.editor.saving': 'Saving…',
    'bannerOptions.editor.error.title': "Couldn't save this banner option.",
    'bannerOptions.editor.error.body': 'Your changes are still here. Try again.',
    'bannerOptions.field.optional': '(optional)',
    'bannerOptions.field.platform': 'Platform',
    'bannerOptions.field.askBy': 'Ask by',
    'bannerOptions.field.askBy.hint': 'Last day the account manager takes requests.',
    'bannerOptions.field.name': 'Name',
    'bannerOptions.field.name.ph': 'e.g. Homepage banner',
    'bannerOptions.field.arabicName': 'Arabic name',
    'bannerOptions.field.arabicName.ph': 'الاسم بالعربية',
    'bannerOptions.field.where': 'Where it appears',
    'bannerOptions.field.where.ph': 'e.g. Top of the home screen, Amman',
    'bannerOptions.field.notes': 'What owners should know',
    'bannerOptions.field.notes.ph':
      'Conditions, who it suits, what the platform needs from the restaurant',
    'bannerOptions.field.runs': 'Runs and fees',
    'bannerOptions.run.from': 'From',
    'bannerOptions.run.to': 'To',
    'bannerOptions.run.fee': 'Fee (JOD)',
    'bannerOptions.run.fee.ph': '45',
    'bannerOptions.run.add': 'Add another run',
    'bannerOptions.run.remove.aria': 'Remove run {n}',
    'bannerOptions.fieldError.name': 'Give the banner a name owners will recognise.',
    'bannerOptions.fieldError.where': 'Say where customers will see it.',
    'bannerOptions.fieldError.runsMin': 'Add at least one run.',
    'bannerOptions.fieldError.runDates': 'Run {n} needs both dates.',
    'bannerOptions.fieldError.runOrder': 'Run {n} ends before it starts.',
    'bannerOptions.fieldError.runFee': 'Run {n} needs a fee above zero.',
    'bannerOptions.preview.heading': 'Owners will see',
    'bannerOptions.preview.empty': 'Add at least one run with dates and a fee to preview it.',
    // Toasts
    'bannerOptions.toast.published':
      'Published. {platform} branches now see “{name}” under Banners you can join.',
    'bannerOptions.toast.publishedShort': 'Published. {platform} branches now see “{name}”.',
    'bannerOptions.toast.draft': "Saved “{name}” as a draft. Owners don't see it yet.",
    'bannerOptions.toast.saved': 'Saved changes to “{name}”.',
    'bannerOptions.toast.movedToDrafts': '“{name}” moved to drafts. Owners no longer see it.',
    'bannerOptions.toast.deleted': 'Deleted “{name}”.',
    // Admin states
    'bannerOptions.loading': 'Loading banner options',
    'bannerOptions.error.title': "Couldn't load banner options.",
    'bannerOptions.error.body': 'Nothing was changed.',
    'bannerOptions.retry': 'Try again',
    'bannerOptions.empty.title': 'No banner options yet',
    'bannerOptions.empty.body': 'Add the first one when an account manager shares a placement offer.',
    'bannerOptions.noMatch.title': 'Nothing matches these filters',
    'bannerOptions.noMatch.body': 'Try another platform or status.',
    // ── Locations (clarity-locations, UI contract brand/locations-admin-mock.html) ──
    'nav.locations': 'Locations',
    'locations.title': 'Locations',
    'locations.sub':
      "Link the same physical branch across platforms into one location, so a store's Talabat, Careem and MyThings numbers read as one place. Admin-maintained; nothing here changes matching, payouts or profit.",
    'locations.client': 'Client',
    'locations.new': 'New location',
    'locations.loading': 'Loading locations',
    'locations.error.title': "Couldn't load locations.",
    'locations.retry': 'Try again',
    'locations.suggest.head': 'Suggested links',
    'locations.suggest.why': 'Same area ({area}) on {platforms}.',
    'locations.suggest.link': 'Link',
    'locations.suggest.dismiss': 'Not the same',
    'locations.list.head': 'Locations',
    'locations.list.edit': 'Edit',
    'locations.list.unlink': 'Unlink',
    'locations.pool.head': 'Not linked yet',
    'locations.pool.empty': 'Every store is linked to a location.',
    'locations.pool.outlet': 'outlet {id}',
    'locations.sel.n': '{count} selected',
    'locations.sel.clear': 'Clear',
    'locations.sel.group': 'Group into a location',
    'locations.editor.titleNew': 'New location',
    'locations.editor.titleEdit': 'Edit location',
    'locations.editor.sub': 'Group the stores that are the same physical branch.',
    'locations.editor.name': 'Name (English)',
    'locations.editor.ar': 'Name (Arabic) — optional',
    'locations.editor.members': 'Stores in this location',
    'locations.editor.cancel': 'Cancel',
    'locations.editor.save': 'Save location',
    'locations.toast.saved': 'Location saved',
    'locations.toast.deleted': 'Location deleted',
    'locations.err.conflict': 'That store is already in another location. Unlink it first.',
    'locations.err.generic': "Couldn't save. Try again.",
    // ── Monthly campaign review (clarity-monthly-campaign-review) ──
    'review.publish.head': 'Publish to owners',
    'review.publish.sub':
      "Send each enabled store's owner their WhatsApp review for this month: what to join, what to skip, and what to ask their account manager. Saving the calendar does not send — publish is the explicit step.",
    'review.publish.month': 'Month',
    'review.publish.status.draft': 'Draft — not sent yet',
    'review.publish.status.published': 'Published {when}',
    'review.publish.recap':
      '{stores} store(s) · {owners} owner(s) will be messaged. Co-fund is modelled at 0% when a sheet does not state it.',
    'review.publish.optOutNote': 'Owners who have not opted in to WhatsApp are skipped, never messaged.',
    'review.publish.action': 'Publish to owners',
    'review.publish.action.pending': 'Sending…',
    'review.publish.confirm.title': 'Send this month to owners?',
    'review.publish.confirm.body':
      'This messages {owners} owner(s) across {stores} store(s). Opt-out owners are skipped. Co-fund is modelled at 0% where unstated.',
    'review.publish.confirm.cancel': 'Not yet',
    'review.publish.confirm.go': 'Send now',
    'review.publish.done': 'Published. {dispatched} sent, {skipped} skipped.',
    'review.publish.doneLink': 'See delivery in Messages',
    'review.publish.error': "Couldn't publish. Try again.",
    // Owner review page.
    'review.title': 'Your campaign review',
    'review.month': 'for {month}',
    'review.loading': 'Loading your review',
    'review.error.title': "Couldn't load your review.",
    'review.notPublished': 'No review has been published for this month yet.',
    'review.coFundNote': "Where a sheet didn't state co-funding, we assumed 0% (the worst case).",
    'review.verdict.join': 'Join',
    'review.verdict.skip': 'Skip',
    'review.verdict.ask_am': 'Ask your AM',
    'review.verdict.join_with_condition': 'Join with a condition',
    'review.marginal': 'marginal',
    'review.expected': 'Expected ~{profit} JOD / order',
    'review.expectedOrders': 'about {low}–{high} orders',
    'review.history': 'Last time ({month}): {orders} orders, {disc} JOD funded',
    'review.history.agrees': 'agrees with the platform',
    'review.similar': 'Similar ({label}): ~{profit} JOD / order over {orders} orders',
    'review.include': 'Include: {items}',
    'review.exclude': 'Keep out: {items}',
    'review.deadline': 'Decide by {date}',
    'review.deadline.late': 'likely too late',
    'review.seeAll': 'See all {count} campaigns',
    'review.seeLess': 'Show fewer',
    'review.brief.head': 'Message for your account manager',
    'review.brief.copy': 'Copy message',
    'review.brief.copied': 'Copied',
    // ── Advisor chat panel (clarity-advisor-chat) ──
    'advisor.launch': 'Ask the advisor',
    'advisor.title': 'Clarity Advisor',
    'advisor.subtitle': 'Online · answers from your data',
    'advisor.teamName': 'Clarity team',
    'advisor.humanSubtitle': 'A specialist is replying to you',
    'advisor.humanBanner': 'A Clarity specialist has joined and is replying to you here.',
    'advisor.provenance': 'Same advisor as WhatsApp · you\u2019re on Clarity web',
    'advisor.provenanceFor': 'Advisor for {client} · same engine as WhatsApp',
    'advisor.close': 'Close',
    'advisor.emptyTitle': 'Hey — I\u2019m your Clarity advisor',
    'advisor.emptyBody': 'Ask me anything about your branches. I read your real orders, payouts and campaigns.',
    'advisor.placeholder': 'Ask about a campaign, a payout, this month…',
    'advisor.send': 'Send',
    'advisor.typing': 'typing…',
    'advisor.briefCopy': 'Copy',
    'advisor.briefCopied': 'Copied ✓',
    'advisor.starter.status': 'What\u2019s the status of my campaign?',
    'advisor.starter.join': 'What should I join this month?',
    'advisor.starter.balance': 'How much is held for me?',
    'advisor.starter.brief': 'Write the message for my AM',
    'advisor.quickActions': 'Quick actions',
    'advisor.chip.status': 'My campaign status',
    'advisor.chip.join': 'What to join',
    'advisor.chip.balance': 'My balance',
    'advisor.chip.brief': 'Message for my AM',
    'advisor.error': 'Couldn\u2019t reach the advisor. Try again.',
    'advisor.verdict.join': 'Join',
    'advisor.verdict.skip': 'Skip',
    'advisor.verdict.ask_am': 'Ask your AM',
    'advisor.verdict.join_with_condition': 'Join with a condition',
    'advisor.marginal': 'marginal',
    // ── AI usage meter (clarity-advisor-llm) ──
    'advisor.ai.remaining': 'AI budget: {remaining} of {cap} left this month',
    'advisor.ai.low': 'AI budget running low · {remaining} left',
    'advisor.ai.capReached': 'AI budget reached for this month — contact us to raise it. The quick answers above still work.',
  },
  ar: {
    'nav.clients': 'العملاء',
    'nav.brands': 'العلامات',
    'nav.workspaceUsers': 'المستخدمون',
    'nav.settings': 'الإعدادات',
    'nav.ingestion': 'الاستيراد',
    'nav.monthlyOffers': 'العروض الشهرية',
    'nav.campaignsOverview': 'نظرة عامة على الحملات',
    'nav.payoutSchedule': 'جدول المدفوعات',
    'nav.payoutBacktest': 'اختبار (تطوير)',
    'nav.subscriptions': 'الفوترة',
    'nav.dashboard': 'لوحة القيادة',
    'nav.orders': 'الطلبات',
    'nav.statements': 'الكشوفات',
    'nav.payouts': 'المدفوعات',
    'nav.campaigns': 'الحملات',
    'nav.insights': 'التحليلات',
    'nav.notifications': 'الإشعارات',
    'nav.catalog': 'الكتالوج',
    'nav.onboarding': 'الإعداد',
    'nav.data': 'البيانات',
    // ── Smart notifications (clarity-smart-notifications) ──
    'notif.title': 'الإشعارات',
    'notif.sub':
      'كل ما يحتاج انتباهك — الحملات والأموال والقائمة وصحة المتجر — يصلك دون أن تبحث عنه.',
    'notif.open': 'افتح الإشعارات',
    'notif.tab.inbox': 'الوارد',
    'notif.tab.reports': 'التقارير',
    'notif.tab.prefs': 'التفضيلات',
    'notif.markAllRead': 'تحديد الكل كمقروء',
    'notif.markRead': 'تحديد كمقروء',
    'notif.viewAll': 'عرض كل الإشعارات',
    'notif.filter.all': 'الكل',
    'notif.filter.action': 'بحاجة قرار',
    'notif.filter.campaign': 'الحملات',
    'notif.filter.money': 'الأموال',
    'notif.filter.menu': 'القائمة',
    'notif.filter.health': 'صحة المتجر',
    'notif.filter.report': 'التقارير',
    'notif.empty.title': 'كل شيء هادئ',
    'notif.empty.body': 'لا شيء يحتاج انتباهك الآن. سننبّهك فور حدوث أمر مهم.',
    'notif.unread': 'غير مقروء',
    'notif.allRead': 'كل شيء مقروء',
    'notif.day.today': 'اليوم',
    'notif.day.yesterday': 'أمس',
    'notif.day.earlier': 'سابقًا',
    'notif.reports.empty': 'لا تقارير بعد. يصلك أول ملخّص وفق الجدول الذي اخترته.',
    'notif.prefs.eventsTitle': 'بماذا تخبرني',
    'notif.prefs.eventsSub': 'اختر اللحظات التي تستحق تنبيهًا، وأين يصلك كل منها.',
    'notif.prefs.reportTitle': 'التقرير الدوري',
    'notif.prefs.reportSub': 'ملخّص مكتوب لترى الصورة كاملة دون أن تسأل.',
    'notif.prefs.cadence': 'كم مرّة',
    'notif.prefs.channelsTitle': 'القنوات',
    'notif.prefs.channelsSub': 'أين يمكن لكلاريتي الوصول إليك. واتساب ودردشة الموقع يتشاركان المحادثة نفسها.',
    'notif.prefs.balanceTitle': 'حدّ تنبيه الرصيد',
    'notif.prefs.balanceSub': 'أخبرني عندما يتجاوز الرصيد المستحق المُقدّر هذا المبلغ.',
    'notif.chan.inapp': 'داخل التطبيق',
    'notif.chan.push': 'إشعار',
    'notif.chan.whatsapp': 'واتساب',
    'notif.cadence.3day': 'كل 3 أيام',
    'notif.cadence.weekly': 'أسبوعي',
    'notif.cadence.monthly': 'شهري',
    'notif.cadence.off': 'إيقاف',
    'notif.push.enable': 'تفعيل إشعارات المتصفّح',
    'notif.push.enabled': 'إشعارات المتصفّح مفعّلة',
    'notif.push.unsupported': 'متصفّحك لا يدعم الإشعارات.',
    'notif.push.blocked': 'الإشعارات محظورة في إعدادات متصفّحك.',
    'notif.save': 'حفظ التفضيلات',
    'notif.saved': 'تم حفظ التفضيلات',
    'notif.event.campaign_finished': 'انتهاء حملة',
    'notif.event.campaigns_ready_for_review': 'حملات جاهزة للمراجعة',
    'notif.event.new_menu_item': 'صنف قائمة جديد للبتّ فيه',
    'notif.event.payout_mismatch': 'دفعة غير مطابقة',
    'notif.event.store_offline': 'فرع صار غير متاح',
    'notif.event.rating_slipped': 'انخفض التقييم',
    'notif.event.ingestion_stalled': 'توقّف متجر عن المزامنة',
    'notif.event.balance_threshold': 'تجاوز الرصيد المستحق حدًّا',
    'rail.manage': 'الإدارة',
    'rail.workspace': 'مساحة العمل',
    'rail.viewing': 'العميل الحالي',
    'rail.back': 'العودة إلى كل العملاء',
    'rail.switch': 'تبديل العميل',
    'topbar.signout': 'تسجيل الخروج',
    'lang.toggle': 'English',
    // ── Scope switcher (clarity-navigation-scope Req 2) ──
    'scope.all': 'كل الفروع',
    'scope.label': 'النطاق',
    'scope.switch': 'تبديل النطاق',
    'scope.open': 'غيّر ما تعرضه',
    'scope.search': 'بحث عن فرع',
    'scope.search.placeholder': 'ابحث بالاسم أو معرّف الفرع',
    'scope.search.empty': 'لا توجد فروع مطابقة لبحثك.',
    'scope.summary.branches': 'فروع',
    'scope.summary.branch': 'فرع',
    'scope.empty': 'لا توجد فروع بعد',
    'scope.empty.cta': 'استيراد علامة',
    'scope.loading': 'جارٍ تحميل الفروع…',
    'scope.lifecycle.live': 'نشط',
    'scope.lifecycle.onboarding': 'قيد الإعداد',
    'scope.lifecycle.disabled': 'معطّل',
    'scope.lifecycle.removed': 'محذوف',
    'scope.lifecycle.aria': 'الحالة: {label}',
    // ── Rail nav (clarity-navigation-scope task 4.2) ──
    'nav.perStore': 'لكل فرع',
    'nav.perStore.aria': 'يُفتح لكل فرع — اختر فرعًا',
    // ── Pick-a-branch prompt (clarity-navigation-scope Req 4.2 / 4.5) ──
    'pickBranch.title': 'اختر فرعًا',
    'pickBranch.subtitle': '{surface} عرضٌ لكل فرع. اختر فرعًا لفتحه.',
    'pickBranch.aria': 'اختر فرعًا لـ {surface}',
    // ── Onboarding lockdown note (clarity-navigation-scope Req 5.2) ──
    'scope.lockdown.note': 'أكمِل إعداد هذا الفرع لإظهار باقي عروضه.',
    // ── Combined-view notes (clarity-navigation-scope Req 6.2) ──
    'scope.combined.onboarding.one': 'فرع واحد ({count}) قيد الإعداد',
    'scope.combined.onboarding.many': '{count} فروع قيد الإعداد',
    // ── Admin ingestion panel (admin-ingestion-control Req 5) ──
    'ingestion.title': 'الاستيراد',
    'ingestion.sub': 'عمليات الاستيراد الأخيرة لكل فرع عبر جميع المستأجرين، الأحدث أولًا.',
    'ingestion.mode.label': 'الوضع',
    'ingestion.mode.daily': 'يومي',
    'ingestion.mode.backfill': 'استيراد كامل',
    'ingestion.mode.reference': 'نافذة مرجعية',
    'ingestion.mode.daily.hint': 'سحب تزايدي منذ آخر استيراد لكل فرع.',
    'ingestion.mode.backfill.hint': 'إعادة سحب كامل لكل السجل.',
    'ingestion.runAll': 'شغّل الآن (كل الفروع)',
    'ingestion.runAll.pending': 'جارٍ الإضافة…',
    'ingestion.rerun': 'إعادة التشغيل',
    'ingestion.rerun.aria': 'إعادة تشغيل {store} في وضع {mode}',
    'ingestion.triggered.one': 'تمت إضافة فرع واحد ({count}) إلى الدفعة {batch}.',
    'ingestion.triggered.many': 'تمت إضافة {count} فروع إلى الدفعة {batch}.',
    'ingestion.triggerError': 'تعذّر بدء التشغيل. حاول مرة أخرى.',
    // Table
    'ingestion.col.store': 'الفرع',
    'ingestion.col.when': 'الوقت',
    'ingestion.col.covered': 'المدة المُستوردة',
    'ingestion.covered.none': '—',
    'ingestion.covered.aria': 'المدة الزمنية للبيانات المُستوردة {range}',
    'ingestion.col.trigger': 'المصدر',
    'ingestion.col.mode': 'الوضع',
    'ingestion.col.result': 'النتيجة',
    'ingestion.col.persisted': 'المحفوظ',
    'ingestion.col.actions': 'إجراءات',
    // Trigger values
    'ingestion.trigger.scheduled': 'مجدول',
    'ingestion.trigger.manual': 'يدوي',
    'ingestion.trigger.reference': 'استيراد التهيئة',
    // Status
    'ingestion.status.running': 'قيد التشغيل',
    'ingestion.status.succeeded': 'نجح',
    'ingestion.status.failed': 'فشل',
    'ingestion.status.skipped': 'تم التخطي',
    'ingestion.status.aria': 'الحالة: {label}',
    // Result detail
    'ingestion.advanced': 'تقدّم',
    'ingestion.notAdvanced': 'لا بيانات جديدة',
    'ingestion.failedSources': 'المصادر الفاشلة: {sources}',
    'ingestion.running.note': 'قيد التنفيذ — يتحدّث هذا الصف عند الانتهاء.',
    // Persisted
    'ingestion.persisted.orders': 'طلبات',
    'ingestion.persisted.statements': 'كشوفات',
    'ingestion.persisted.payouts': 'مدفوعات',
    'ingestion.persisted.provisioned': 'مُجهّزة',
    'ingestion.persisted.campaigns': 'حملات',
    'ingestion.persisted.matched': 'مطابَقة',
    'ingestion.persisted.rawJson': 'نص حرفي',
    'ingestion.persisted.aria':
      '{orders} طلبات، {statements} كشوفات، {payouts} مدفوعات، {provisioned} عناصر مُجهّزة، {campaigns} حملات، {matched} طلبات مطابَقة، {rawJson} محفوظة حرفيًا',
    // Empty
    'ingestion.empty': 'لا توجد عمليات استيراد بعد. اضغط «شغّل الآن» لبدء واحدة.',
    // Retry affordances
    'ingestion.retry': 'إعادة المحاولة',
    'ingestion.retry.pending': 'جارٍ إعادة المحاولة…',
    'ingestion.retry.aria': 'إعادة محاولة {store}',
    'ingestion.retryAllFailed': 'إعادة محاولة كل الفاشلة',
    'ingestion.retryAllFailed.pending': 'جارٍ إعادة المحاولة…',
    'ingestion.retryAllFailed.count': 'إعادة محاولة كل الفاشلة ({count})',
    'ingestion.summary.running': '{count} قيد التشغيل',
    'ingestion.summary.failed': '{count} فشل',
    'ingestion.summary.succeeded': '{count} نجح',
    'ingestion.card.when': 'بدأ',
    'ingestion.card.covered': 'المدة المُستوردة',
    'ingestion.card.nothing': 'لم يُحفظ شيء بعد.',
    // ── Admin monthly-offer review (clarity-campaign-matching Req 3.3, 5.2, 10.3) ──
    'offerReview.title': 'العروض الشهرية',
    'offerReview.sub':
      'ارفع ورقة عروض مدير الحساب، راجع المسودة وصحّحها، ثم احفظها. العروض الشهرية مشتركة بين كل فروع المنصة ومؤرّخة الفعالية — الشهر الجديد يُضاف ولا يستبدل القديم.',
    'offerReview.platform': 'المنصة',
    'offerReview.image': 'صورة ورقة العروض',
    'offerReview.intake.hint':
      'اختر منصة وصورة ورقة العروض. نقرأها بالتعرّف الضوئي إلى مسودة قابلة للتحرير؛ ومع إيقاف التعرّف الضوئي تُدخل العروض يدويًا.',
    'offerReview.ocr.pending': 'جارٍ قراءة ورقة العروض…',
    'offerReview.ocr.error': 'تعذّرت قراءة الصورة. جرّب صورة أخرى أو أدخل العروض يدويًا.',
    'offerReview.banner.ocr': 'تم استخراجها من الصورة. راجع كل حقل مقابل الورقة قبل الحفظ.',
    'offerReview.banner.noKey': 'التعرّف الضوئي متوقف، فالمسودة فارغة — أضف كل عرض من الورقة يدويًا أدناه.',
    'offerReview.banner.noKeyNonEmpty': 'التعرّف الضوئي متوقف؛ أُدخلت هذه العروض يدويًا. راجعها قبل الحفظ.',
    'offerReview.start.title': 'ابدأ مسودة عروض شهرية',
    'offerReview.start.sub': 'ارفع ورقة عروض لقراءتها ضوئيًا، أو ابدأ مسودة فارغة وأدخل العروض يدويًا.',
    'offerReview.start.manual': 'أدخل العروض يدويًا',
    'offerReview.editor.title': 'راجع المسودة',
    'offerReview.editor.count.one': 'عرض واحد ({count})',
    'offerReview.editor.count.many': '{count} عروض',
    'offerReview.editor.empty': 'لا توجد عروض في المسودة بعد. أضف عرضًا للبدء.',
    'offerReview.offer.add': 'أضف عرضًا',
    'offerReview.offer.aria': 'العرض {n}',
    'offerReview.offer.heading': 'العرض {n}',
    'offerReview.offer.remove': 'إزالة',
    'offerReview.offer.remove.aria': 'إزالة العرض {n}',
    'offerReview.field.name': 'الاسم',
    'offerReview.field.name.ph': 'مثال: فلاش سيل لجميع الزبائن',
    'offerReview.field.arabic': 'الاسم بالعربية',
    'offerReview.field.arabic.ph': 'الاسم بالعربية',
    'offerReview.field.audience': 'الجمهور',
    'offerReview.audience.all': 'كل الزبائن',
    'offerReview.audience.plus': 'مشتركو Plus فقط',
    'offerReview.audience.nonplus': 'غير المشتركين فقط',
    'offerReview.field.discount': 'نسبة الخصم %',
    'offerReview.field.cap': 'حد الخصم الأقصى',
    'offerReview.field.cofund': 'نسبة المشاركة بالتمويل %',
    'offerReview.field.minOrder': 'الحد الأدنى للطلب',
    'offerReview.field.dealKey': 'مفتاح صفقة المنصة',
    'offerReview.field.dealKey.ph': 'مثال: LQTA-2026',
    'offerReview.field.dealKey.hint':
      'اربط صفقة المنصة المطابقة لدمج العرض الشهري مع صفقته في حملة واحدة.',
    'offerReview.occ.title': 'التواريخ',
    'offerReview.occ.count.one': 'تاريخ واحد ({count})',
    'offerReview.occ.count.many': '{count} تواريخ',
    'offerReview.occ.empty': 'لا توجد تواريخ بعد — أضف كل فترة تشغيل لهذا العرض.',
    'offerReview.occ.add': 'أضف تاريخًا',
    'offerReview.occ.aria': 'التاريخ {n}',
    'offerReview.occ.start': 'البداية',
    'offerReview.occ.end': 'النهاية',
    'offerReview.occ.discount': 'نسبة الخصم % (تجاوز)',
    'offerReview.occ.cap': 'الحد (تجاوز)',
    'offerReview.occ.inherit': 'يرث من العرض',
    'offerReview.occ.remove': 'إزالة',
    'offerReview.occ.remove.aria': 'إزالة التاريخ {n}',
    'offerReview.save': 'احفظ العروض الشهرية',
    'offerReview.save.pending': 'جارٍ الحفظ…',
    'offerReview.save.error': 'تعذّر الحفظ. راجع المسودة وحاول مجددًا.',
    'offerReview.result.title': 'تم الحفظ',
    'offerReview.result.lede':
      'تم حفظ {campaigns} حملات و{occurrences} تواريخ، مشتركة بين كل فروع {platform}.',
    'offerReview.result.warnings': 'تحذيرات',
    'offerReview.saved.tagged': 'موسوم {key}',
    'offerReview.saved.untagged': 'غير موسوم',
    // ── Current offers catalog (read of what's saved) ──
    'offerReview.current.title': 'العروض الحالية',
    'offerReview.current.sub': '· المعروض الآن لـ{platform}',
    'offerReview.current.effNote': '{count} · النسخ الحالية · مشتركة بين كل الفروع',
    'offerReview.current.one': 'عرض واحد',
    'offerReview.current.many': '{count} عروض',
    'offerReview.current.filter': 'تصفية العروض…',
    'offerReview.current.loading': 'جارٍ تحميل العروض المحفوظة…',
    'offerReview.current.error': 'تعذّر تحميل العروض المحفوظة.',
    'offerReview.current.empty':
      'لا توجد عروض حالية لهذه المنصة. ارفع ورقة أو أدخل العروض يدويًا لبدء الكتالوج.',
    'offerReview.current.noFilterMatch': 'لا توجد عروض مطابقة للتصفية.',
    'offerReview.current.ranged': 'نطاق',
    'offerReview.current.tproOnly': 'TPRO فقط',
    'offerReview.current.newOnly': 'الجدد فقط',
    'offerReview.current.noDiscount': 'بدون خصم محدد',
    'offerReview.current.off': 'خصم {disc}',
    'offerReview.current.cap': 'حد {cap} دينار',
    'offerReview.current.runs.one': 'تشغيل واحد',
    'offerReview.current.runs.many': '{count} تشغيلات',
    'offerReview.current.occHeading': 'الفترات',
    'offerReview.current.occCol': 'الفترة',
    'offerReview.current.occOverrideCol': 'تجاوز لكل تشغيل',
    'offerReview.current.inherits': 'يرث من العرض',
    'offerReview.current.expand': 'إظهار فترات {name}',
    'offerReview.current.filterPlatform': 'تصفية حسب المنصة',
    'offerReview.current.allPlatforms': 'كل المنصات',
    // ── List-primary restructure: action buttons + modals ──
    'offerReview.action.add': 'إضافة حملة',
    'offerReview.action.import': 'استيراد حملات',
    'offerReview.add.title': 'إضافة عرض شهري',
    'offerReview.add.sub': 'أدخل عرضًا واحدًا يدويًا. يُشارك بين فروع المنصة ومؤرّخ الفعالية.',
    'offerReview.import.title': 'استيراد حملات من ورقة',
    'offerReview.import.sub':
      'ارفع ورقة عروض مدير الحساب. نقرأها ضوئيًا إلى مسودة قابلة للتحرير تراجعها قبل الحفظ.',
    'offerReview.import.dropTitle': 'اختر صورة ورقة العروض',
    'offerReview.import.dropHint': 'JPEG أو PNG. نقرأها ضوئيًا إلى مسودة تراجعها قبل الحفظ.',
    'offerReview.modal.close': 'إغلاق',
    'offerReview.field.discountMin': 'الخصم — الحد الأدنى %',
    'offerReview.field.discountMax': 'الخصم — الحد الأقصى % (اختياري)',
    // ── Admin connections (clarity-platform-abstraction Req 5) ──
    'nav.connections': 'الاتصالات',
    'connections.title': 'اتصالات المنصّات',
    'connections.adminOnly': 'للمشرف فقط',
    'connections.lede':
      'توثّق Clarity كل منصّة بالطريقة التي تعمل بها تلك المنصّة. تقرأ Talabat وCareem جلسة متصفّح حيّة تبقى مسجّلاً الدخول إليها؛ أمّا MyThings فتسجّل الدخول لكل متجر ببيانات اعتماد مخزَّنة. لا يُعرض شيء حسّاس هنا — الحالات تصنيفٌ فقط.',
    'connections.authModel': 'جلسة متصفّح · Chrome',
    'connections.groupSession': 'منصّات جلسة المتصفّح',
    'connections.authRest': 'بيانات اعتماد مخزَّنة · REST',
    'connections.storesCount': '{n} متجر · كلٌّ يسجّل دخوله بنفسه',
    'connections.detailRest':
      'كل متجر يسجّل الدخول إلى MyThings بحسابه الخاص. يبقى المتجر متّصلاً ما دامت بياناته المخزَّنة تُنتج رمزاً.',
    'connections.notSignedIn': '— لم يُسجّل الدخول بعد',
    'connections.enterCreds': 'إدخال البيانات',
    'connections.update': 'تحديث البيانات',
    'connections.storeState.connected': 'متّصل',
    'connections.storeState.expired': 'انتهى الدخول',
    'connections.storeState.needs-credentials': 'بحاجة إلى بيانات',
    'connections.storeState.checking': 'جارٍ الفحص…',
    'connections.storeState.error': 'خطأ في الاتصال',
    'connections.retry': 'إعادة محاولة الدخول',
    'connections.state.needs-credentials': 'بحاجة إلى بيانات',
    'connections.state.partial': 'بحاجة إلى إجراء',
    'connections.state.checking': 'جارٍ الفحص…',
    'connections.cred.titleNew': 'ربط المتجر',
    'connections.cred.titleUpd': 'تحديث البيانات',
    'connections.cred.leadNew':
      'أدخل حساب MyThings الذي يسجّل به هذا المتجر الدخول. تخزّنه Clarity مشفّراً وتعيد استخدامه لإبقاء جلسة المتجر حيّة — ولا يُعرض مجدداً.',
    'connections.cred.leadUpd':
      'حدّث بيانات دخول MyThings لهذا المتجر. تحلّ كلمة المرور الجديدة محلّ المخزَّنة؛ وتعيد Clarity تسجيل الدخول فوراً للتأكّد.',
    'connections.cred.loginLabel': 'حساب الدخول',
    'connections.cred.pwLabel': 'كلمة المرور',
    'connections.cred.reveal': 'إظهار',
    'connections.cred.hide': 'إخفاء',
    'connections.cred.note':
      'تُخزَّن بتشفير قابل للعكس (لا تُعرض مجدداً) لأن Clarity يجب أن تعيد استخدامها لإنتاج رمز وصول جديد عند انتهاء القديم.',
    'connections.cred.save': 'حفظ واتصال',
    'connections.cred.update': 'تحديث وإعادة اتصال',
    'connections.cred.cancel': 'إلغاء',
    'connections.checked': 'آخر فحص',
    'connections.loading': 'جارٍ قراءة الجلسات الحيّة…',
    'connections.error': 'تعذّر تحميل حالة الاتصال. حاول مرة أخرى.',
    'connections.empty.title': 'لا توجد منصّات نشطة بعد',
    'connections.empty.sub': 'تظهر المنصّة هنا بمجرّد ربط موصّلها ووضع علامة نشط عليها.',
    'connections.reconnect': 'إعادة الاتصال',
    'connections.howItWorks': 'كيف يعمل',
    'connections.howItWorks.aria': 'كيف يعمل اتصال {platform}',
    // حالات الاتصال (نصّ، وليس اللون وحده — WCAG AA)
    'connections.state.connected': 'متّصل',
    'connections.state.expired': 'انتهت الجلسة',
    'connections.state.disconnected': 'غير متّصل',
    'connections.state.aria': 'حالة الاتصال: {label}',
    // شريط الاتصالات في الواجهة (يُسمّي كل منصّة نشطة بنقطة حالة — Req 5.3)
    'connections.rollup.checking': 'جارٍ فحص الاتصالات…',
    'connections.rollup.unavailable': 'الاتصالات غير متاحة',
    'connections.rollup.aria': 'اتصالات المنصّات — افتح لوحة الاتصالات',
    'connections.rollup.title': 'افتح اتصالات المنصّات',
    // جملة التفاصيل أسفل رأس كل بطاقة
    'connections.detail.connected': 'توجد جلسة بوابة مسجّلة الدخول قابلة للقراءة في Chrome المرتبط.',
    'connections.detail.expired':
      'الجلسة لم تعُد صالحة — يجب أن يعيد شخصٌ تسجيل الدخول في Chrome المرتبط لإعادة الاتصال.',
    'connections.detail.disconnected':
      'لا توجد جلسة: طبقة المتصفّح متوقّفة، أو Chrome غير متاح، أو لا توجد علامة تبويب للبوابة مفتوحة.',
    // تدفّق إعادة التوثيق المشترك (بلا لصق — الاسترداد تلقائي)
    'connections.reauth.title': 'إعادة الاتصال بـ {platform}',
    'connections.reauth.lead':
      'يتم التحقق من {platform} عبر ملفات تعريف الارتباط خلف حماية مكافحة الروبوتات، فلا شيء للصقه — تقرأ Clarity الجلسة من نافذة Chrome التي تبقى مسجّلاً الدخول إليها.',
    'connections.reauth.step1': 'افتح نافذة Chrome المرتبطة على مضيف الاستحواذ.',
    'connections.reauth.step2': 'اذهب إلى بوابة شركاء {platform} وسجّل الدخول كالمعتاد.',
    'connections.reauth.step3':
      'اترك علامة التبويب مفتوحة — تُبقي Clarity الجلسة دافئة وتعيد قراءتها تلقائيًا.',
    'connections.reauth.recovery':
      'الاسترداد تلقائي. بمجرّد عودتك لتسجيل الدخول، يحوّل فحص الحالة التالي هذه المنصّة إلى متّصل — لا شيء للصقه أو رفعه أو تخزينه.',
    'connections.reauth.close': 'إغلاق',
    'connections.reauth.openChrome': 'افتح Chrome المرتبط',
    // ── Manual MyThings add (mythings-manual-add Req 10) ──
    'addMythings.card.title': 'إضافة متجر MyThings',
    'addMythings.card.body':
      'أدخل العلامة وبيانات دخول MyThings لكل فرع. تسجّل Clarity الدخول وتجلب معرّف المتجر واسمه تلقائيًا وتربطه فورًا — دون جلسة Chrome.',
    'addMythings.card.meta': 'MyThings · بيانات اعتماد مخزَّنة',
    'addMythings.card.open': 'إضافة متجر MyThings',
    'addMythings.sheet.title': 'إضافة متجر MyThings',
    'addMythings.sheet.client': 'العميل',
    'addMythings.sheet.clientPlaceholder': 'اختر عميلًا…',
    'addMythings.sheet.brand': 'اسم العلامة',
    'addMythings.sheet.brandPlaceholder': 'مثال: برجر بوتيك',
    'addMythings.sheet.brandHint': 'السلسلة التي ينتمي إليها هذا المتجر. تُضاف الفروع أدناه.',
    'addMythings.sheet.branches': 'الفروع',
    'addMythings.sheet.branch': 'فرع',
    'addMythings.sheet.branchName': 'اسم الفرع',
    'addMythings.sheet.branchNamePlaceholder': 'مثال: عبدون',
    'addMythings.sheet.substore': 'معرّف متجر MyThings',
    'addMythings.sheet.substoreHint': '(SubStoreId)',
    'addMythings.sheet.substorePlaceholder': '17755',
    'addMythings.sheet.login': 'حساب الدخول',
    'addMythings.sheet.loginPlaceholder': 'store@mythingsjo.com',
    'addMythings.sheet.password': 'كلمة المرور',
    'addMythings.sheet.reveal': 'إظهار',
    'addMythings.sheet.hide': 'إخفاء',
    'addMythings.sheet.removeBranch': 'إزالة',
    'addMythings.sheet.addBranch': 'إضافة فرع آخر',
    'addMythings.sheet.callout':
      'نسجّل الدخول بكل حساب الآن لجلب معرّف المتجر واسمه من MyThings — لا تُدخلها بنفسك. تُخزَّن بيانات كل دخول بتشفير قابل للعكس وتُعاد لإبقاء الفرع مسجّلاً الدخول، ولا تُعرض مجددًا.',
    'addMythings.sheet.count.one': 'فرع واحد ({count})',
    'addMythings.sheet.count.many': '{count} فروع',
    'addMythings.sheet.cancel': 'إلغاء',
    'addMythings.sheet.submit': 'إضافة واتصال',
    'addMythings.sheet.submitting': 'جارٍ الاتصال…',
    'addMythings.sheet.error': 'تعذّرت إضافة المتجر. راجع الفروع وحاول مجددًا.',
    'addMythings.done.title': '{brand} متّصل',
    'addMythings.done.sub.one': 'تمت إضافة فرع واحد ({count}) وتسجيل دخوله. يمكنك إدارته في أي وقت من الاتصالات.',
    'addMythings.done.sub.many': 'تمت إضافة {count} فروع وتسجيل دخولها. يمكنك إدارتها في أي وقت من الاتصالات.',
    'addMythings.done.connected': 'متّصل',
    'addMythings.done.expired': 'انتهى الدخول',
    'addMythings.done.error': 'خطأ في الاتصال',
    'addMythings.done.unnamed': 'فرع',
    'addMythings.done.notSignedIn': 'لم يُسجّل الدخول بعد',
    'addMythings.done.close': 'إغلاق',
    'addMythings.done.goConnections': 'الذهاب إلى الاتصالات',
    // Entry hub header (two "add" entry cards)
    'addHub.title': 'إضافة متجر',
    'addHub.lede':
      'اكتشف المتاجر من منصّة تسجّل الدخول إليها (Talabat، Careem)، أو أضف متجر MyThings يدويًا — تسجّل MyThings الدخول لكل متجر ببياناته الخاصة، فلا شيء يُكتشف تلقائيًا.',
    'addHub.import.title': 'اكتشاف واستيراد',
    'addHub.import.body':
      'اسحب علامة وفروعها من حساب منصّة تبقى مسجّلاً الدخول إليه في Chrome المرتبط.',
    'addHub.import.meta': 'Talabat · Careem',
    // ── Admin payout schedule (clarity-payout-schedule Req 5) ──
    'payoutSchedule.title': 'جدول المدفوعات',
    'payoutSchedule.sub':
      'جدول مدفوعات طلبات لكل شهر — فترات التغطية ومواعيد صرف كل منها. يُحفظ مرة واحدة ويُشارك بين كل فروع المنصة.',
    'payoutSchedule.add': '+ أضف جدولًا',
    'payoutSchedule.list.platform': 'المنصة',
    'payoutSchedule.list.month': 'الشهر',
    'payoutSchedule.list.rows': 'الصفوف',
    'payoutSchedule.list.coverage': 'التغطية',
    'payoutSchedule.list.updated': 'آخر تحديث',
    'payoutSchedule.list.edit': 'تعديل',
    'payoutSchedule.list.showPayouts': 'عرض المدفوعات',
    'payoutSchedule.list.hidePayouts': 'إخفاء المدفوعات',
    'payoutSchedule.list.empty.title': 'لا توجد جداول بعد',
    'payoutSchedule.list.empty.body': 'أضف جدول مدفوعات طلبات الشهري ليظهر لكل فرع موعد دفعته القادمة.',
    'payoutSchedule.list.loading': 'جارٍ تحميل الجداول…',
    'payoutSchedule.list.error': 'تعذّر تحميل الجداول. حاول مجددًا.',
    'payoutSchedule.modal.addTitle': 'أضف جدول مدفوعات',
    'payoutSchedule.modal.editTitle': 'تعديل جدول المدفوعات',
    'payoutSchedule.modal.close': 'إغلاق',
    'payoutSchedule.cancel': 'إلغاء',
    'payoutSchedule.platform': 'المنصة',
    'payoutSchedule.month': 'الشهر',
    'payoutSchedule.col.type': 'النوع',
    'payoutSchedule.col.from': 'التغطية من',
    'payoutSchedule.col.to': 'التغطية إلى',
    'payoutSchedule.col.payout': 'تاريخ الحوالة',
    'payoutSchedule.col.label': 'التسمية',
    'payoutSchedule.col.actions': 'إجراءات',
    'payoutSchedule.type.window': 'فترة تغطية',
    'payoutSchedule.type.closing': 'إغلاق شهري',
    'payoutSchedule.row': 'صف {n}',
    'payoutSchedule.labelPlaceholder': 'ملاحظة اختيارية',
    'payoutSchedule.addRow': '+ أضف صفًا',
    'payoutSchedule.removeRow': 'احذف الصف',
    'payoutSchedule.save': 'احفظ الجدول',
    'payoutSchedule.saving': 'جارٍ الحفظ…',
    'payoutSchedule.saveError': 'تعذّر الحفظ. راجع الصفوف وحاول مجددًا.',
    'payoutSchedule.saved': 'تم حفظ {count} صفوف، مشتركة بين كل فروع المنصة.',
    'payoutSchedule.hint':
      'انسخ جدول طلبات (فترة التغطية ← تاريخ الحوالة). صف «الإغلاق الشهري» يسوّي الشهر السابق ولا يحتاج فترة تغطية.',
    // ── Payout estimate backtest (INTERNAL dev tool — English-leaning labels) ──
    'backtest.title': "اختبار مطابقة المدفوعات",
    'backtest.dev': "تطوير",
    'backtest.sub': "يقارن تفصيل دفعة طلبات بندًا ببند مع نفس البنود المبنية من طلبات فترة الدفعة — فيُظهر الفارق البند المختلف بالضبط.",
    'backtest.stat.passRate': "نسبة النجاح",
    'backtest.stat.passRateSub': "{pass} من {judged} دفعة ضمن ±{tol}%",
    'backtest.stat.tested': "مدفوعات مُختبرة",
    'backtest.stat.testedSub': "مسوّاة، عبر {stores} متجر",
    'backtest.stat.passing': "ناجحة",
    'backtest.stat.passingSub': "صافينا ضمن ±{tol}% من صافي طلبات",
    'backtest.stat.failing': "فاشلة",
    'backtest.stat.failingSub': "صافينا منحرف بأكثر من ±{tol}%",
    'backtest.tolerance': "التفاوت ±",
    'backtest.show': "عرض",
    'backtest.show.all': "كل المدفوعات",
    'backtest.show.fail': "الفاشلة فقط",
    'backtest.show.pass': "الناجحة فقط",
    'backtest.col.payout': "الدفعة",
    'backtest.col.period': "الفترة",
    'backtest.col.paidOn': "تاريخ الدفع",
    'backtest.col.orders': "الطلبات",
    'backtest.col.talabatNet': "صافي طلبات",
    'backtest.col.ourNet': "صافينا",
    'backtest.col.delta': "الفارق",
    'backtest.col.deltaPct': "الفارق %",
    'backtest.col.verdict': "النتيجة",
    'backtest.verdict.pass': "ناجح",
    'backtest.verdict.fail': "فاشل",
    'backtest.detail.window': "تغطي {from} ← {to}",
    'backtest.detail.paidOn': "تاريخ الدفع",
    'backtest.detail.orders': "الطلبات: {linked} من {expected} مربوطة ({cancelled} ملغاة)",
    'backtest.detail.noBreakdown': "لم يُجلب تفصيل طلبات لهذه الدفعة — أعد تشغيل الاستيراد الكامل لجلبه",
    'backtest.lines.line': "البند",
    'backtest.lines.talabat': "طلبات",
    'backtest.lines.ours': "حسابنا (من الطلبات)",
    'backtest.lines.diff': "حسابنا − طلبات",
    'backtest.lines.accountLevel': "على مستوى الحساب",
    'backtest.lines.legend': "المبالغ بإشارتها كما تُبلغ طلبات (الخصومات سالبة). بنود «على مستوى الحساب» لا يقابلها طلب، فلا يمكن بناؤها من الطلبات — وتُظهر ما أضافته طلبات أو خصمته بنفسها. التعديلات تحمل جولة متخطاة (أقل من الحد الأدنى للدفع): نبنيها من طلبات تلك الجولة مطروحاً منها إنفاق إعلانات طلبات (+16% ضريبة) وتقدير لرسوم العروض الممولة التي لا تشملها تقارير الإعلانات.",
    'backtest.line.sales': "المبيعات (الإجمالي + الضريبة)",
    'backtest.line.talabatVoucher': "قسائم تموّلها طلبات",
    'backtest.line.commission': "العمولة",
    'backtest.line.paymentFee': "رسوم الدفع",
    'backtest.line.proDelivery': "رسوم توصيل برو",
    'backtest.line.operational': "رسوم وقت الانتظار",
    'backtest.line.ads': "الإعلانات / التسويق",
    'backtest.line.refundCharges': "رسوم استرداد العملاء",
    'backtest.line.adjustments': "التعديلات (جولة مُرحّلة)",
    'backtest.carry.orders': "جولة متخطاة {from} ← {to} · {n} طلبات",
    'backtest.carry.inferred': "البداية مستنتجة من أول طلب مسجّل",
    'backtest.carry.ads': "إعلانات غير مُبلغ عنها بعد (تقديرية) · {n} × {rate} لكل طلب",
    'backtest.carry.reportedAds': "إنفاق الإعلانات من تقارير طلبات (+16% ضريبة)",
    'backtest.carry.perOrder': "تسويق لكل طلب موجود مسبقاً في التصدير",
    'backtest.carry.deals': "رسوم عروض تقديرية · {n} × {rate} لكل طلب",
    'backtest.adspend.reported': "الجولات المتخطاة مُسعّرة بإنفاق إعلانات طلبات ({from} ← {to})؛ رسوم العروض الممولة فقط تقديرية",
    'backtest.adspend.unavailable': "لم يُجلب إنفاق إعلانات طلبات ({reason})، لذا إعلانات الجولات المتخطاة تقديرية",
    'backtest.carry.tag': "+ جولة مُرحّلة",
    'backtest.carry.none': "لا توجد جولة متخطاة قبل هذه الدفعة",
    'backtest.min.label': "حد أدنى {min} دينار",
    'backtest.min.holds': "القاعدة منطبقة",
    'backtest.min.breaks': "القاعدة لا تنطبق",
    'backtest.min.paidBelow': "دُفع {amount}، أقل من الحد الأدنى",
    'backtest.min.carriedAbove': "رُحّل {amount}، يساوي الحد الأدنى أو يزيد",
    'backtest.stat.minRule': "قاعدة الحد الأدنى",
    'backtest.stat.minRuleSub': "قاعدة {min} دينار تنطبق على {held} من {total} دفعة",
    'backtest.line.otherAdditions': "إضافات أخرى",
    'backtest.line.otherDeductions': "خصومات أخرى",
    'backtest.line.net': "صافي الدفعة",
    'backtest.empty': "لا توجد مدفوعات مسوّاة للاختبار بعد.",
    'backtest.noMatch': "لا مدفوعات تطابق هذا المرشّح.",
    'backtest.loading': "جارٍ تشغيل الاختبار…",
    'backtest.error': "تعذّر تحميل الاختبار.",
    'backtest.store.meta': "{n} مدفوعات · {platform}",
    // ── Banner options (key groups listed in the EN block) ──
    'nav.bannerOptions': 'خيارات البانرات',
    'nav.campaigns.newCount.one': '{count} خيار بانر جديد',
    'nav.campaigns.newCount.many': '{count} خيارات بانرات جديدة',
    'bannerOptions.platform.talabat': 'طلبات',
    'bannerOptions.platform.careem': 'كريم',
    'bannerOptions.platform.mythings': 'MyThings',
    // Shared row
    'banners.untitled': 'بانر بلا اسم',
    'banners.where.placeholder': 'مكان الظهور',
    'banners.run.dates': '{from} – {to}',
    'banners.run.days.one': '{count} يوم',
    'banners.run.days.many': '{count} أيام',
    'banners.run.fee': '{fee} دينار',
    'banners.run.runningNow': 'يُعرض الآن',
    'banners.fee.range': '{min}–{max}',
    'banners.fee.currency': 'دينار',
    'banners.fee.perRun': 'لكل فترة',
    'banners.fee.forTheRun': 'للفترة',
    'banners.badge.new': 'جديد',
    'banners.badge.askBy': 'اطلبه قبل {date}',
    // Owner tab
    'banners.tab': 'بانرات يمكنك الانضمام إليها',
    'banners.about.offering': 'هذه مواضع إعلانية مدفوعة تعرضها {platform} على المطاعم.',
    'banners.about.noSignup': 'Clarity لا يسجّلك فيها',
    'banners.about.ask': '؛ إن ناسبك أحدها، فاسأل مدير حسابك في {platform}.',
    'banners.about.fees': 'الرسوم هي ما تتقاضاه {platform} عن كل فترة عرض.',
    'banners.group.upcoming': 'قريبًا',
    'banners.group.upcoming.count.one': '{count} خيار',
    'banners.group.upcoming.count.many': '{count} خيارات',
    'banners.group.running': 'يُعرض الآن',
    'banners.group.running.sub': 'ما زال بإمكانك السؤال عن الفترة التالية',
    'banners.join.open': 'كيف تنضم',
    'banners.join.hide': 'إخفاء',
    'banners.join.lead.before': 'لا يستطيع Clarity تسجيلك.',
    'banners.join.lead.manager': 'مدير حسابك في {platform}',
    'banners.join.lead.after': 'هو من يفعل ذلك. أرسل له هذه الرسالة، أو قلها بكلماتك:',
    'banners.join.message':
      'مرحبًا، أودّ الانضمام إلى «{name}» على {platform} ({where}) للفترة {from} – {to} مقابل {fee} دينار.\nالفرع: {branch} ({externalId}).\nما الذي تحتاجونه منّا؟',
    'banners.join.copy': 'نسخ الرسالة',
    'banners.join.copied': 'تم النسخ',
    'banners.loading': 'جارٍ تحميل خيارات البانرات',
    'banners.error.title': 'تعذّر تحميل خيارات البانرات.',
    'banners.error.body': 'حملاتك لم تتأثر.',
    'banners.retry': 'حاول مجددًا',
    'banners.empty.title': 'لا توجد بانرات معروضة على {platform} حاليًا',
    'banners.empty.body':
      'عندما تعرض {platform} موضعًا إعلانيًا مدفوعًا، يضيفه فريق Clarity هنا وستراه مُعلَّمًا بـ«جديد».',
    'banners.notice.count.one': '{count} خيار بانر جديد',
    'banners.notice.count.many': '{count} خيارات بانرات جديدة',
    'banners.notice.on': 'على {platform}.',
    'banners.notice.see': 'عرض البانرات',
    // Admin page
    'bannerOptions.title': 'خيارات البانرات',
    'bannerOptions.sub':
      'مواضع إعلانية مدفوعة تعرضها المنصة هذا الموسم. انشر خيارًا وسيراه كل فرع على تلك المنصة في الحملات ← بانرات يمكنك الانضمام إليها. ينضم المالكون عبر مدير حسابهم، لا من خلال Clarity.',
    'bannerOptions.add': 'إضافة خيار بانر',
    'bannerOptions.filter.platform.aria': 'تصفية حسب المنصة',
    'bannerOptions.filter.platform.all': 'كل المنصات',
    'bannerOptions.filter.status.aria': 'تصفية حسب الحالة',
    'bannerOptions.filter.status.all': 'الكل',
    'bannerOptions.filter.status.published': 'منشورة',
    'bannerOptions.filter.status.draft': 'مسودات',
    'bannerOptions.filter.status.ended': 'منتهية',
    'bannerOptions.count.one': '{count} خيار',
    'bannerOptions.count.many': '{count} خيارات',
    'bannerOptions.row.askBy': 'اطلبه قبل {date}',
    'bannerOptions.row.noAskBy': 'بلا موعد أخير للطلب',
    'bannerOptions.row.runs.one': '{count} فترة',
    'bannerOptions.row.runs.many': '{count} فترات',
    'bannerOptions.runs.col.run': 'الفترة',
    'bannerOptions.runs.col.days': 'الأيام',
    'bannerOptions.runs.col.fee': 'الرسوم',
    'bannerOptions.reach.draft': 'غير ظاهر للمالكين بعد.',
    'bannerOptions.reach.ended': 'انتهى — لم يعد يظهر للمالكين.',
    'bannerOptions.reach.published': 'يظهر لكل فروع {platform}.',
    'bannerOptions.state.draft': 'مسودة',
    'bannerOptions.state.published': 'منشور',
    'bannerOptions.state.ended': 'منتهٍ',
    'bannerOptions.action.publish': 'نشر',
    'bannerOptions.action.moveToDrafts': 'نقل إلى المسودات',
    'bannerOptions.action.edit': 'تعديل',
    'bannerOptions.action.delete': 'حذف',
    'bannerOptions.action.error': 'تعذّر تحديث «{name}». حاول مجددًا.',
    // Editor
    'bannerOptions.editor.titleAdd': 'إضافة خيار بانر',
    'bannerOptions.editor.titleEdit': 'تعديل «{name}»',
    'bannerOptions.editor.sub':
      'سيراه المالكون على هذه المنصة كخيار يمكنهم طلب الانضمام إليه من مدير حسابهم.',
    'bannerOptions.editor.close': 'إغلاق',
    'bannerOptions.editor.cancel': 'إلغاء',
    'bannerOptions.editor.saveDraft': 'حفظ كمسودة',
    'bannerOptions.editor.moveToDrafts': 'نقل إلى المسودات',
    'bannerOptions.editor.publish': 'نشر',
    'bannerOptions.editor.saveChanges': 'حفظ التغييرات',
    'bannerOptions.editor.delete': 'حذف',
    'bannerOptions.editor.saving': 'جارٍ الحفظ…',
    'bannerOptions.editor.error.title': 'تعذّر حفظ خيار البانر هذا.',
    'bannerOptions.editor.error.body': 'تغييراتك ما زالت هنا. حاول مجددًا.',
    'bannerOptions.field.optional': '(اختياري)',
    'bannerOptions.field.platform': 'المنصة',
    'bannerOptions.field.askBy': 'آخر موعد للطلب',
    'bannerOptions.field.askBy.hint': 'آخر يوم يستقبل فيه مدير الحساب الطلبات.',
    'bannerOptions.field.name': 'الاسم',
    'bannerOptions.field.name.ph': 'مثلًا: بانر الصفحة الرئيسية',
    'bannerOptions.field.arabicName': 'الاسم بالعربية',
    'bannerOptions.field.arabicName.ph': 'الاسم بالعربية',
    'bannerOptions.field.where': 'مكان الظهور',
    'bannerOptions.field.where.ph': 'مثلًا: أعلى الشاشة الرئيسية، عمّان',
    'bannerOptions.field.notes': 'ما يجب أن يعرفه المالكون',
    'bannerOptions.field.notes.ph': 'الشروط، ولمن يناسب، وما تحتاجه المنصة من المطعم',
    'bannerOptions.field.runs': 'فترات العرض والرسوم',
    'bannerOptions.run.from': 'من',
    'bannerOptions.run.to': 'إلى',
    'bannerOptions.run.fee': 'الرسوم (دينار)',
    'bannerOptions.run.fee.ph': '45',
    'bannerOptions.run.add': 'إضافة فترة أخرى',
    'bannerOptions.run.remove.aria': 'إزالة الفترة {n}',
    'bannerOptions.fieldError.name': 'أعطِ البانر اسمًا يعرفه المالكون.',
    'bannerOptions.fieldError.where': 'حدّد أين سيراه العملاء.',
    'bannerOptions.fieldError.runsMin': 'أضف فترة واحدة على الأقل.',
    'bannerOptions.fieldError.runDates': 'الفترة {n} تحتاج إلى تاريخَي البداية والنهاية.',
    'bannerOptions.fieldError.runOrder': 'الفترة {n} تنتهي قبل أن تبدأ.',
    'bannerOptions.fieldError.runFee': 'الفترة {n} تحتاج إلى رسوم أكبر من صفر.',
    'bannerOptions.preview.heading': 'ما سيراه المالكون',
    'bannerOptions.preview.empty': 'أضف فترة واحدة على الأقل بتواريخ ورسوم لمعاينتها.',
    // Toasts
    'bannerOptions.toast.published':
      'تم النشر. فروع {platform} ترى الآن «{name}» في بانرات يمكنك الانضمام إليها.',
    'bannerOptions.toast.publishedShort': 'تم النشر. فروع {platform} ترى الآن «{name}».',
    'bannerOptions.toast.draft': 'حُفظ «{name}» كمسودة. لا يراه المالكون بعد.',
    'bannerOptions.toast.saved': 'حُفظت التغييرات على «{name}».',
    'bannerOptions.toast.movedToDrafts': 'نُقل «{name}» إلى المسودات. لم يعد المالكون يرونه.',
    'bannerOptions.toast.deleted': 'حُذف «{name}».',
    // Admin states
    'bannerOptions.loading': 'جارٍ تحميل خيارات البانرات',
    'bannerOptions.error.title': 'تعذّر تحميل خيارات البانرات.',
    'bannerOptions.error.body': 'لم يتغيّر شيء.',
    'bannerOptions.retry': 'حاول مجددًا',
    'bannerOptions.empty.title': 'لا توجد خيارات بانرات بعد',
    'bannerOptions.empty.body': 'أضف أول خيار عندما يشارك مدير حساب عرض موضع إعلاني.',
    'bannerOptions.noMatch.title': 'لا شيء يطابق هذه المرشّحات',
    'bannerOptions.noMatch.body': 'جرّب منصة أو حالة أخرى.',
    // ── Locations (clarity-locations) ──
    'nav.locations': 'الفروع',
    'locations.title': 'الفروع',
    'locations.sub':
      'اربط نفس الفرع الفعلي عبر المنصات بفرع واحد، حتى تطلع أرقام طلبات وكريم وماي ثينغز لنفس المحل كأنها محل واحد. بتتدار من الأدمن؛ ما بتأثر على المطابقة أو الدفعات أو الربح.',
    'locations.client': 'العميل',
    'locations.new': 'فرع جديد',
    'locations.loading': 'جاري تحميل الفروع',
    'locations.error.title': 'ما قدرنا نحمّل الفروع.',
    'locations.retry': 'حاول مرة ثانية',
    'locations.suggest.head': 'روابط مقترحة',
    'locations.suggest.why': 'نفس المنطقة ({area}) على {platforms}.',
    'locations.suggest.link': 'اربط',
    'locations.suggest.dismiss': 'مش نفس المحل',
    'locations.list.head': 'الفروع',
    'locations.list.edit': 'تعديل',
    'locations.list.unlink': 'فك الربط',
    'locations.pool.head': 'لسا ما انربطت',
    'locations.pool.empty': 'كل المحلات مربوطة بفرع.',
    'locations.pool.outlet': 'رقم المحل {id}',
    'locations.sel.n': '{count} محدد',
    'locations.sel.clear': 'إلغاء',
    'locations.sel.group': 'اجمعها بفرع',
    'locations.editor.titleNew': 'فرع جديد',
    'locations.editor.titleEdit': 'تعديل فرع',
    'locations.editor.sub': 'اجمع المحلات اللي هي نفس الفرع الفعلي.',
    'locations.editor.name': 'الاسم (إنجليزي)',
    'locations.editor.ar': 'الاسم (عربي) — اختياري',
    'locations.editor.members': 'المحلات بهالفرع',
    'locations.editor.cancel': 'إلغاء',
    'locations.editor.save': 'احفظ الفرع',
    'locations.toast.saved': 'انحفظ الفرع',
    'locations.toast.deleted': 'انحذف الفرع',
    'locations.err.conflict': 'هالمحل مربوط بفرع ثاني. فك الربط الأول.',
    'locations.err.generic': 'ما قدرنا نحفظ. حاول مرة ثانية.',
    // ── Monthly campaign review (clarity-monthly-campaign-review) ──
    'review.publish.head': 'إرسال للأصحاب',
    'review.publish.sub':
      'ابعت لكل صاحب محل مفعّل مراجعته على واتساب لهذا الشهر: شو ينضم له، شو يتخطى، وشو يسأل مدير حسابه. حفظ التقويم ما بيرسل — الإرسال خطوة صريحة.',
    'review.publish.month': 'الشهر',
    'review.publish.status.draft': 'مسودة — ما انبعتت بعد',
    'review.publish.status.published': 'انبعتت {when}',
    'review.publish.recap':
      'رح يوصل {stores} محل · {owners} صاحب. التمويل المشترك محسوب 0% إذا الورقة ما ذكرته.',
    'review.publish.optOutNote': 'الأصحاب اللي ما وافقوا على واتساب بينتخطّوا، ما بينبعتلهم.',
    'review.publish.action': 'إرسال للأصحاب',
    'review.publish.action.pending': 'عم نبعت…',
    'review.publish.confirm.title': 'نبعت هالشهر للأصحاب؟',
    'review.publish.confirm.body':
      'رح يوصل {owners} صاحب عبر {stores} محل. اللي ما وافقوا بينتخطّوا. التمويل المشترك محسوب 0% وين ما انذكر.',
    'review.publish.confirm.cancel': 'مش هلأ',
    'review.publish.confirm.go': 'ابعت الآن',
    'review.publish.done': 'انبعتت. {dispatched} انبعت، {skipped} انتخطّى.',
    'review.publish.doneLink': 'شوف التوصيل في الرسائل',
    'review.publish.error': 'ما قدرنا نبعت. حاول مرة ثانية.',
    // Owner review page.
    'review.title': 'مراجعة حملاتك',
    'review.month': 'لشهر {month}',
    'review.loading': 'جاري تحميل مراجعتك',
    'review.error.title': 'ما قدرنا نحمّل مراجعتك.',
    'review.notPublished': 'ما انبعتت مراجعة لهذا الشهر بعد.',
    'review.coFundNote': 'وين الورقة ما ذكرت التمويل المشترك، افترضنا 0% (أسوأ حالة).',
    'review.verdict.join': 'انضم',
    'review.verdict.skip': 'تخطَّ',
    'review.verdict.ask_am': 'اسأل مدير حسابك',
    'review.verdict.join_with_condition': 'انضم بشرط',
    'review.marginal': 'حدّي',
    'review.expected': 'متوقع ~{profit} دينار / طلب',
    'review.expectedOrders': 'حوالي {low}–{high} طلب',
    'review.history': 'آخر مرة ({month}): {orders} طلب، {disc} دينار مموّل',
    'review.history.agrees': 'متوافق مع المنصة',
    'review.similar': 'مشابه ({label}): ~{profit} دينار / طلب عبر {orders} طلب',
    'review.include': 'اشمل: {items}',
    'review.exclude': 'استثنِ: {items}',
    'review.deadline': 'قرّر قبل {date}',
    'review.deadline.late': 'غالباً فات الأوان',
    'review.seeAll': 'شوف كل الـ{count} حملة',
    'review.seeLess': 'اعرض أقل',
    'review.brief.head': 'رسالة لمدير حسابك',
    'review.brief.copy': 'انسخ الرسالة',
    'review.brief.copied': 'تم النسخ',
    // ── Advisor chat panel (clarity-advisor-chat) ──
    'advisor.launch': 'اسأل المستشار',
    'advisor.title': 'مستشار كلاريتي',
    'advisor.subtitle': 'متصل · يجاوب من بياناتك',
    'advisor.teamName': 'فريق كلاريتي',
    'advisor.humanSubtitle': 'أحد المختصين يرد عليك',
    'advisor.humanBanner': 'انضم أحد مختصي كلاريتي ويرد عليك هنا.',
    'advisor.provenance': 'نفس المستشار اللي على واتساب · أنت على كلاريتي ويب',
    'advisor.provenanceFor': 'مستشار {client} · نفس محرك واتساب',
    'advisor.close': 'إغلاق',
    'advisor.emptyTitle': 'مرحبا — أنا مستشار كلاريتي',
    'advisor.emptyBody': 'اسألني أي شي عن فروعك. بقرأ طلباتك ودفعاتك وحملاتك الحقيقية.',
    'advisor.placeholder': 'اسأل عن حملة، دفعة، أو هذا الشهر…',
    'advisor.send': 'إرسال',
    'advisor.typing': 'يكتب…',
    'advisor.briefCopy': 'انسخ',
    'advisor.briefCopied': 'تم النسخ ✓',
    'advisor.starter.status': 'شو وضع حملتي؟',
    'advisor.starter.join': 'شو أنضم له هذا الشهر؟',
    'advisor.starter.balance': 'قديش ماسكين إلي؟',
    'advisor.starter.brief': 'اكتب رسالة لمدير حسابي',
    'advisor.quickActions': 'إجراءات سريعة',
    'advisor.chip.status': 'وضع حملتي',
    'advisor.chip.join': 'شو أنضم',
    'advisor.chip.balance': 'رصيدي',
    'advisor.chip.brief': 'رسالة لمدير حسابي',
    'advisor.error': 'ما قدرنا نوصل للمستشار. حاول مرة ثانية.',
    'advisor.verdict.join': 'انضم',
    'advisor.verdict.skip': 'تخطّ',
    'advisor.verdict.ask_am': 'اسأل مديرك',
    'advisor.verdict.join_with_condition': 'انضم بشرط',
    'advisor.marginal': 'حدّي',
    // ── AI usage meter (clarity-advisor-llm) ──
    'advisor.ai.remaining': 'رصيد الذكاء: باقي {remaining} من {cap} هذا الشهر',
    'advisor.ai.low': 'رصيد الذكاء قارب يخلص · باقي {remaining}',
    'advisor.ai.capReached': 'وصلت لحد الذكاء لهذا الشهر — تواصل معنا لرفعه. الأجوبة السريعة فوق لسا شغّالة.',
  },
};

const LOCALE_TAG: Record<Locale, string> = { en: 'en', ar: 'ar-JO' };
const STORAGE_KEY = 'clarity.locale';

/** Values interpolated into a string's `{name}` placeholders. */
type TranslateVars = Record<string, string | number>;

interface I18nValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  localeTag: string;
  /**
   * Translate a key, optionally filling `{name}` placeholders from `vars` (used
   * by the scope strings, e.g. `t('pickBranch.subtitle', { surface })`). Missing
   * placeholders are left untouched so a key typo is visible rather than silent.
   */
  t: (key: keyof (typeof DICT)['en'], vars?: TranslateVars) => string;
  setLocale: (next: Locale) => void;
  toggleLocale: () => void;
}

/** Replace every `{name}` in `template` with `vars[name]` (left as-is if absent). */
function interpolate(template: string, vars?: TranslateVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

const I18nCtx = createContext<I18nValue | null>(null);

function loadLocale(): Locale {
  if (typeof localStorage === 'undefined') return 'en';
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'ar' ? 'ar' : 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(loadLocale);
  const dir: 'ltr' | 'rtl' = locale === 'ar' ? 'rtl' : 'ltr';

  // Keep the module-level formatter locale in sync synchronously on render so the
  // first paint after a locale change already formats money/dates correctly.
  setActiveLocale(LOCALE_TAG[locale]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = LOCALE_TAG[locale];
      document.documentElement.dir = dir;
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, locale);
    }
  }, [locale, dir]);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      dir,
      localeTag: LOCALE_TAG[locale],
      t: (key, vars) =>
        interpolate(DICT[locale][key] ?? DICT.en[key] ?? String(key), vars),
      setLocale: setLocaleState,
      toggleLocale: () => setLocaleState((l) => (l === 'en' ? 'ar' : 'en')),
    }),
    [locale, dir],
  );

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error('useI18n must be used within an I18nProvider');
  return ctx;
}

/** Test-only handle on the raw dictionary (EN/AR parity checks). */
export const DICT_FOR_TEST = DICT;
